/**
 * App-wide i18n for the SyriaPHR portal (en | ar).
 *
 * Tiny module-level store (no context, no deps): components read via `t()`
 * during render; the App shell subscribes with `useLang()` so a language
 * change re-renders the whole tree and every `t()` call re-evaluates.
 * `document.documentElement.dir/lang` is kept in sync for RTL CSS + a11y.
 *
 * Clinical code-system terms (LOINC/RxNorm/SNOMED names, drug names) are
 * intentionally NOT translated — they are canonical data, not UI chrome.
 */
import { useSyncExternalStore } from 'react'

export type Lang = 'en' | 'ar'

const STORAGE_KEY = '***'

type Dict = Record<string, string>

const en: Dict = {
  // shell
  'app.tagline': 'National personal health record',
  'shell.patient': 'Patient',
  'shell.provider': 'Provider',
  'shell.toggleLangAria': 'Switch language',
  'shell.arabicLabel': 'عربي',
  'shell.englishLabel': 'English',
  'shell.switchToArabic': 'Switch interface to Arabic',
  'shell.switchToEnglish': 'Switch interface to English',
  'shell.rosterActive': 'Provider roster active',
  'shell.verifiedNhi': 'Verified NHI SYR-2026-000001',
  'shell.homeAria': 'SyriaPHR home',
  'shell.primaryNav': 'Primary',
  'nav.home': 'Home',
  'nav.register': 'Register Patient',
  'nav.search': 'Search',
  'nav.roster': 'Roster',
  'nav.nhiRegister': 'NHI Register',
  'nav.myChart': 'My Chart',
  'nav.myNHI': 'My NHI',
  'nav.myIdCard': 'My Chart',
  'nav.patientMode': 'Patient mode',
  // domains
  'domain.labs.title': 'Laboratory',
  'domain.labs.subtitle': 'Orders and results normalized to LOINC.',
  'domain.meds.title': 'Medications',
  'domain.meds.subtitle': 'RxNorm records with ingredient-level mapping readiness.',
  'domain.diagnoses.title': 'Diagnoses',
  'domain.diagnoses.subtitle': 'Problem list entries coded to ICD-10 (WHO international edition).',
  'domain.allergies.title': 'Allergies',
  'domain.allergies.subtitle': 'Allergy and adverse reaction records in SNOMED CT.',
  'domain.procedures.title': 'Procedures',
  'domain.procedures.subtitle': 'Procedure entries using OPCS-4, CCAM, and SBCS candidates.',
  // domain pages
  'domain.addEntry': 'Add Entry',
  'domain.newEntry': 'New {title} Entry',
  'domain.closeForm': 'Close form',
  'domain.saveEntry': 'Save Entry',
  'domain.cancel': 'Cancel',
  'domain.patientRecord': 'Patient Record',
  'domain.lookupTable': 'Lookup Table',
  'domain.searchPlaceholder': 'Search code, term, system',
  'domain.searchLookupAria': 'Search lookup table',
  'domain.clearSearch': 'Clear search',
  'domain.searching': 'Searching…',
  'domain.resultsFor': 'Results for “{q}”',
  'domain.allTerms': 'All terms',
  'domain.nResults': '{n} result',
  'domain.nResultsPlural': '{n} results',
  'domain.use': 'Use',
  'domain.useTermAria': 'Use term',
  'domain.useToPrefillAria': 'Use {name} to prefill the form',
  'domain.clickToPrefill': 'Click to prefill the form',
  'domain.noMatchingTerms': 'No matching terms — clear the search to see the full list.',
  'domain.nEntries': '{n} entry',
  'domain.nEntriesPlural': '{n} entries',
  'domain.noEntries': 'No entries yet — use “Add Entry” or click a row in the lookup table to create one.',
  'domain.actions': 'Actions',
  'domain.deleteAria': 'Delete {name}',
  'domain.entrySaved': '{title} entry saved to the patient record.',
  'domain.entryWord': 'entry',
  'domain.addedToForm': 'added to form',
  'domain.selectPlaceholder': 'Select…',
  'domain.searchCode': 'Search or enter code',
  'domain.enterName': 'Enter name',
  'domain.codeShort': 'Code',
  'domain.codeNameRequired': 'Code and name are required for every entry.',
  'domain.noFutureDates': 'Dates cannot be in the future.',
  'domain.medDescRequired': 'A medication description is required for every entry.',
  'domain.clinicianSigRequired': 'Doctor-reported entries require at least one discrete SIG field (dose, route, form, or frequency).',
  // generic field labels (shared across domain forms/columns)
  'field.code': 'Code',
  'field.name': 'Name',
  'field.status': 'Status',
  'field.value': 'Value',
  'field.unit': 'Unit',
  'field.date': 'Date',
  'field.range': 'Range',
  'field.since': 'Since',
  'field.system': 'System',
  'field.component': 'Component',
  'field.scale': 'Scale',
  'field.test': 'Test',
  'field.observation': 'Observation',
  'field.preferredTerm': 'Preferred Term',
  'field.category': 'Category',
  'field.condition': 'Condition',
  'field.allergen': 'Allergen',
  'field.reaction': 'Reaction',
  'field.severity': 'Severity',
  'field.procedure': 'Procedure',
  'field.provenance': 'Provenance',
  'field.note': 'Note',
  'field.medication': 'Medication',
  'field.howTake': 'How do you take it?',
  'field.dose': 'Dose',
  'field.doseUnit': 'Dose unit',
  'field.route': 'Route',
  'field.form': 'Form',
  'field.frequency': 'Frequency',
  'field.duration': 'Duration',
  'field.quantity': 'Quantity',
  'field.refills': 'Refills',
  'field.indication': 'Indication',
  'field.prescriber': 'Prescriber',
  'field.sigNote': 'SIG note',
  'field.provenanceToggle': 'Reported by',
  'field.patientReported': 'Patient reported',
  'field.prescribed': 'Prescribed',
  'sig.qty': 'Qty',
  'sig.refill': 'refill',
  'sig.refills': 'refills',
  // status vocabulary (pill + select options)
  'status.active': 'Active',
  'status.paused': 'Paused',
  'status.completed': 'Completed',
  'status.stopped': 'Stopped',
  'status.resolved': 'Resolved',
  'status.historic': 'Historic',
  'status.inactive': 'Inactive',
  'status.enteredInError': 'Entered in error',
  'status.mild': 'Mild',
  'status.moderate': 'Moderate',
  'status.severe': 'Severe',
  'status.lifeThreatening': 'Life-threatening',
  'status.planned': 'Planned',
  'status.cancelled': 'Cancelled',
  'status.draft': 'Draft',
  // dashboard
  'dash.identity': 'Identity verification',
  'dash.tierModel': '4-tier assurance model',
  'dash.tiersAria': 'Verification tiers',
  'dash.recentActivity': 'Recent activity',
  'dash.timelineAria': 'Recent health timeline',
  'dash.scrollMore': 'Scroll for more',
  'dash.vitalsAria': 'Vitals trends',
  'dash.domainsAria': 'Record domains',
  'dash.systolicBp': 'Systolic BP',
  'dash.diastolicBp': 'Diastolic BP',
  'dash.weight': 'Weight',
  'dash.hba1c': 'HbA1c',
  'dash.trendVsLast': '{delta} vs last visit',
  'dash.trendThisMonth': '{delta} this month',
  'dash.trendAria': '{label} trend over recent visits',
  'dash.demoNote': 'Follow-up on HbA1c (7.4%). Discussed dietary adjustments and adherence to metformin. Patient engaged; plan to recheck HbA1c in 3 months and review BP control at next visit.',
  'dash.directive.livingWillSummary': 'Organ donor preference documented',
  'dash.directive.dnrSummary': 'Awaiting family meeting',
  'dash.toastNoteSoon': 'Note composer coming soon — backend not wired yet.',
  'dash.toastNotesSoon': 'Full clinical notes view coming soon.',
  'dash.documentsProof': 'Documents & proof',
  'dash.documentsProofSummary': 'Vaccine QR cards (SMART Health Card · SMART Health Link) and shareable proof of vaccination.',
  'dash.encDocs': 'Encounters & documents',
  'dash.encDocsSummary': 'Visit history and filed documents',
  'dash.identityDetails': 'Identity details',
  'dash.showMore': 'Show more',
  'dash.showLess': 'Show less',
  'dash.lastUpdated': 'Updated {date}',
  'dash.clinicalSectionsAria': 'Clinical record sections',
  'dash.assuranceModel': 'How identity assurance levels work',
  'dash.openDomain': 'Open {title}',
  'tl.hba1c.title': 'HbA1c result',
  'tl.hba1c.detail': '7.4% · Damascus Central Lab',
  'tl.hba1c.time': '2h ago',
  'tl.metformin.title': 'Metformin refilled',
  'tl.metformin.detail': '500 mg · 60 tablets',
  'tl.metformin.time': '1d ago',
  'tl.diagnosis.title': 'Diagnosis updated',
  'tl.diagnosis.detail': 'CKD stage 3 · status Active',
  'tl.diagnosis.time': '3d ago',
  'tl.procedure.title': 'Procedure recorded',
  'tl.procedure.detail': 'Appendectomy · OPCS-4 H22.9',
  'tl.procedure.time': '1w ago',
  'tl.allergy.title': 'Allergy reviewed',
  'tl.allergy.detail': 'Penicillin · severity Moderate',
  'tl.allergy.time': '2w ago',
  'tl.checkup.title': 'Annual check-up',
  'tl.checkup.detail': 'General medicine · Al-Mouwasat',
  'tl.checkup.time': '1mo ago',
  'tier.desc1': 'Identity self-declared at registration. No independent proof yet.',
  'tier.desc2': 'Matched against MOI records with a weak confidence score.',
  'tier.desc3': 'MOI card swipe or manual verification by a registered clerk.',
  'tier.desc4': 'Highest assurance — biometric or authoritative source confirmation.',
  // portal (register/search/profile)
  'portal.eyebrow': 'Minimum Registry · Sprint 2',
  'portal.registerTitle': 'Register Patient',
  'portal.searchTitle': 'Search Patients',
  'portal.registerDesc': 'Create a new patient record with a structured Arabic + English name and identifier.',
  'portal.searchDesc': 'Find an existing patient by name and date of birth, with MPI match scores.',
  'portal.register': 'Register',
  'portal.search': 'Search',
  'portal.registeredOk': 'Patient registered — NHI {nhi} ({action})',
  'portal.action.createdNew': 'created new record',
  'portal.action.linkedExisting': 'linked to existing record',
  'reg.subtitle': 'New patient · Arabic + English · Sprint 2',
  'reg.arabicName': 'Arabic name (الاسم بالعربية)',
  'reg.latinName': 'Latin name (English)',
  'reg.nationalIdLabel': 'National ID (11 digits)',
  'reg.passportLabel': 'Passport number',
  'reg.select': 'Select…',
  'reg.cityPlaceholder': 'Mazzeh',
  'reg.submitting': 'Registering…',
  'reg.nameRequired': 'Enter a name in Arabic and/or Latin.',
  'reg.nidDigits': 'National ID must be exactly 11 digits.',
  'reg.failed': 'Registration failed. Please try again.',
  'search.nameAr': 'Arabic name',
  'search.nameLatin': 'Latin name',
  'search.submit': 'Search',
  'search.needInput': 'Enter a name and/or date of birth to search.',
  'search.failed': 'Search failed.',
  'search.candidatesPlural': '{n} candidates found.',
  'search.review': 'Review',
  'search.dobPrefix': 'DOB ',
  'profile.failed': 'Failed to load patient.',
  'profile.tierLine': 'Verification tier: Level {n} — {name}',
  // banner
  'banner.photoAlt': 'Patient photo — Ahmad Al-Hassan',
  'banner.verifiedBadge': 'Identity verified — Level 4',
  'banner.male': 'Male',
  'banner.female': 'Female',
  'banner.copyNhi': 'Copy NHI to clipboard',
  'banner.levelShort': 'Level {n}: {name}',
  'banner.note': 'Note',
  'banner.notes': 'Notes',
  'banner.viewAllergyAria': 'View {name} in Allergies',
  'banner.reviewBeforePrescribing': '⚠️ Review before prescribing',
  'banner.allergyType.drug': 'drug',
  'banner.allergyType.food': 'food',
  'banner.allergyType.environmental': 'environmental',
  'banner.vitals': 'Vitals',
  'banner.moreVitals': '+{n} more',
  'banner.directiveType.living-will': 'Living will',
  'banner.directiveType.dnr-discussion': 'DNR discussion',
  'banner.activeBadge': '✓ Active',
  'banner.draftBadge': '… Draft',
  'banner.manage': 'Manage',
  'banner.cancel': 'Cancel',
  'banner.ageYears': '{n}y',
  'vital.height': 'Height',
  'vital.weight': 'Weight',
  'vital.bmi': 'BMI',
  'vital.bp': 'Blood Pressure',
  'vital.hba1c': 'HbA1c',

  // Add-vitals panel (patient-entered vitals with SNOMED mapping)
  'vitals.title': 'Vitals log',
  'vitals.aria': 'Vitals log',
  'vitals.nReadings': '{n} readings',
  'vitals.loading': 'Loading vitals…',
  'vitals.loadFailed': 'Vitals could not be loaded. Check the API and try again.',
  'vitals.empty': 'No vitals on record yet.',
  'vitals.addVital': 'Add vital',
  'vitals.closeForm': 'Close form',
  'vitals.formTitle': 'Record a vital',
  'vitals.formHint': 'Self-entered readings are tagged as patient-reported.',
  'vitals.type': 'Vital type',
  'vitals.type.blood_pressure_systolic': 'Blood pressure — systolic',
  'vitals.type.blood_pressure_diastolic': 'Blood pressure — diastolic',
  'vitals.type.height': 'Height',
  'vitals.type.weight': 'Weight',
  'vitals.type.o2_saturation': 'Oxygen saturation (SpO₂)',
  'vitals.type.pain_level': 'Pain level',
  'vitals.bloodPressurePair': 'Blood pressure (systolic + diastolic)',
  'vitals.value': 'Value',
  'vitals.valueSystolic': 'Systolic',
  'vitals.valueDiastolic': 'Diastolic',
  'vitals.unit': 'Unit',
  'vitals.when': 'Date & time',
  'vitals.whenHint': 'Defaults to now — edit only if the reading was taken earlier.',
  'vitals.submit': 'Save vital',
  'vitals.submitting': 'Saving…',
  'vitals.cancel': 'Cancel',
  'vitals.saved': 'Vital recorded.',
  'vitals.saveFailed': 'The vital could not be saved.',
  'vitals.err.required': 'Enter a value.',
  'vitals.err.number': 'Enter a numeric value.',
  'vitals.err.range': 'Value must be between {min} and {max} {unit}.',
  'vitals.err.pair': 'Enter both systolic and diastolic values.',
  'vitals.err.datetime': 'Enter a valid date and time.',
  'vitals.err.o2Range': 'SpO₂ must be between 0 and 100%.',
  'vitals.err.painRange': 'Pain level must be between 0 and 10.',
  'vitals.col.vital': 'Vital',
  'vitals.col.value': 'Value',
  'vitals.col.snomed': 'SNOMED',
  'vitals.col.when': 'Recorded',
  'vitals.col.source': 'Source',
  'vitals.source.patient': 'Patient',
  'vitals.source.clinician': 'Clinician',
  'vitals.source.import': 'Imported',
  'vitals.snomedAria': 'SNOMED CT code {code}',
  // portable proof + encounters + documents
  'proof.subtitle': 'SMART Health Card · SMART Health Link',
  'proof.shcTitle': 'SMART Health Card',
  'proof.shcAlt': 'COVID-19 SMART Health Card QR code',
  'proof.shlTitle': 'SMART Health Link',
  'proof.shlAlt': 'SMART Health Link QR code',
  'proof.modalShc': 'COVID-19 SMART Health Card',
  'proof.modalShl': 'SMART Health Link',
  'proof.shcModalDesc': 'Scan to verify the COVID-19 vaccination record offline. The card is signed by the issuer (ES256) and carries only the patient\u2019s birth date + per-dose vaccine facts.',
  'proof.passcodeNote': 'Passcode {code} (dev/seed only). Link expires 48h after creation.',
  'enc.title': 'Recent encounters',
  'enc.aria': 'Recent encounters',
  'enc.nVisits': '{n} visits',
  'enc.type.emergency': 'Emergency',
  'enc.type.outpatient': 'Outpatient',
  'enc.type.inpatient': 'Inpatient',
  'enc.type.telehealth': 'Telehealth',
  'enc.type.home': 'Home',
  'enc.reason.enc-1': 'Chest pain, rule out ACS',
  'enc.location.enc-1': 'Al-Mouwasat University Hospital · ER',
  'enc.reason.enc-2': 'Diabetes follow-up (HbA1c 7.4%)',
  'enc.location.enc-2': 'Al-Mouwasat University Hospital · Endocrine',
  'enc.reason.enc-3': 'Medication adherence review',
  'enc.location.enc-3': 'Telehealth',
  'enc.disposition.home': 'Home',
  'doc.title': 'Documents',
  'doc.aria': 'Documents',
  'doc.nFiles': '{n} files',
  'doc.type.discharge-summary': 'Discharge Summary',
  'doc.type.lab-report': 'Lab Report',
  'doc.type.imaging-report': 'Imaging Report',
  'doc.type.referral-letter': 'Referral Letter',
  'doc.type.scanned-paper': 'Scanned Paper',
  'doc.type.consent': 'Consent',
  'doc.type.insurance': 'Insurance',
  'doc.type.other': 'Other',
  'doc.title.doc-1': 'Discharge summary — ER visit (2026-08-27)',
  'doc.title.doc-2': 'Scanned lab report — CBC + HbA1c',
  'doc.title.doc-3': 'Chest X-ray report',
  'doc.pdf': 'PDF',
  'doc.image': 'Image',
  // encounter detail panel + states (FHIR-aligned view, ADR-015)
  'enc.loading': 'Loading encounters…',
  'enc.loadFailed': 'Encounters could not be loaded. Check the API and try again.',
  'enc.empty': 'No encounters on record yet.',
  'enc.openAria': 'Open encounter: {reason}',
  'enc.icd10ChipAria': 'ICD-10-WHO code (WHO international edition)',
  'enc.panelTitle': 'Encounter',
  'enc.field.type': 'Type',
  'enc.field.status': 'Status',
  'enc.field.period': 'Period',
  'enc.field.reason': 'Reason',
  'enc.field.practitioner': 'Practitioner',
  'enc.field.location': 'Location',
  'enc.field.disposition': 'Discharge disposition',
  'enc.notesHeading': 'Clinical notes',
  'enc.noNotes': 'No notes recorded on this encounter.',
  'enc.status.planned': 'Planned',
  'enc.status.in-progress': 'In progress',
  'enc.status.finished': 'Finished',
  'enc.status.cancelled': 'Cancelled',
  'enc.status.entered-in-error': 'Entered in error',
  'enc.noteType.progress': 'Progress note',
  'enc.noteType.admission': 'Admission note',
  'enc.noteType.discharge-summary': 'Discharge summary',
  'enc.noteType.op-note': 'Operative note',
  'enc.noteType.nursing': 'Nursing note',
  'enc.noteType.phone': 'Phone note',
  'enc.noteType.free-text': 'Free-text note',
  'enc.noteSource.clinician': 'Clinician',
  'enc.noteSource.patient': 'Patient',
  'enc.noteSource.import': 'Imported',
  'enc.signed.draft': 'Draft',
  'enc.signed.signed': 'Signed',
  'enc.signed.amended': 'Amended',
  // AI-Scribe "Speak encounter" dock
  'scribe.title': 'Speak encounter',
  'scribe.newEncounter': 'New encounter',
  'scribe.close': 'Close',
  'scribe.encounterType': 'Encounter type',
  'scribe.facility': 'Facility',
  'scribe.periodStart': 'Start time',
  'scribe.reason': 'Reason for visit',
  'scribe.reasonHint': 'Optional — e.g. diabetes follow-up',
  'scribe.startRecording': 'Start recording',
  'scribe.stopRecording': 'Stop recording',
  'scribe.recording': 'Recording…',
  'scribe.transcribing': 'Transcribing and structuring the note…',
  'scribe.transcribingHint': 'This usually takes 10–60 seconds.',
  'scribe.transcript': 'Transcript',
  'scribe.noteLabel': 'Draft note (edit before committing)',
  'scribe.suggestions': 'Suggested terms',
  'scribe.commit': 'Commit encounter',
  'scribe.committing': 'Committing…',
  'scribe.reviewHint': 'Review and edit the note — you are signing it as the clinician.',
  'scribe.committed': 'Encounter saved to the chart.',
  'scribe.committedHint': 'The encounter is now listed under Recent encounters.',
  'scribe.viewEncounter': 'View encounter',
  'scribe.toastCommitted': 'Spoken encounter committed to the chart.',
  'scribe.micError': 'Microphone unavailable — check browser permissions and try again.',
  'scribe.failed': 'Scribe step failed during {at}. The audio is kept locally until you retry.',
  'scribe.stage.transcribing': 'transcription',
  'scribe.stage.committing': 'commit',
  'scribe.retry': 'Retry',
  'doc.loading': 'Loading documents…',
  'doc.loadFailed': 'Documents could not be loaded. Check the API and try again.',
  'doc.empty': 'No documents on record yet.',
  'doc.openAria': 'Open document: {title}',
  'doc.panelTitle': 'Document',
  'doc.text': 'Text',
  'doc.field.filename': 'Filename',
  'doc.field.mime': 'Type (MIME)',
  'doc.field.size': 'Size',
  'doc.field.sha256': 'SHA-256',
  'doc.field.uploadedBy': 'Uploaded by',
  'doc.field.uploadedAt': 'Uploaded at',
  'doc.field.retention': 'Retention class',
  'doc.field.encounter': 'Linked encounter',
  'doc.field.contentStatus': 'Content status',
  'doc.noEncounter': 'Not linked to an encounter',
  'doc.sizeB': '{n} B',
  'doc.sizeKB': '{n} KB',
  'doc.sizeMB': '{n} MB',
  'doc.retention.legal': 'Legal',
  'doc.retention.clinical': 'Clinical',
  'doc.retention.administrative': 'Administrative',
  'doc.contentStatus.available': 'Available',
  'doc.contentStatus.missing': 'Missing',
  'doc.contentStatus.expired': 'Expired',
  'doc.bodyFollowup': 'Document content opens from the storage tier in a follow-up release; this panel shows the full metadata record.',
  'panel.close': 'Close panel',
  'panel.loading': 'Loading details…',
  'panel.loadFailed': 'The details could not be loaded. Close and try again.',
  'tier.name1': 'Declared',
  'tier.name2': 'Verified — weak',
  'tier.name3': 'Verified — strong',
  'tier.name4': 'Authoritative',
  'tier.short1': 'Declared',
  'tier.short2': 'Weakly Verified',
  'tier.short3': 'Strongly Verified',
  'tier.short4': 'Authoritative',
  'explain.launcher': 'Explain my chart',
  'explain.launcherDesc': 'Open a read-only, conversational explanation for {patientName}. Answers always show cited chart sections, and Arabic switches the panel to RTL.',
  'prov.roster': 'Provider roster',
  'prov.allPatients': 'All patients',
  'prov.rosterDesc': 'Search the roster and open any patient. This PoC shows all patients with no facility scoping.',
  'prov.backToRoster': 'Back to roster',
  'prov.patientList': 'Patient list',
  'prov.loadingRoster': 'Loading roster…',
  'prov.onePatient': '{n} patient',
  'prov.manyPatients': '{n} patients',
  'prov.searchRoster': 'Search roster',
  'prov.searchAll': 'Search all patients',
  'prov.searchHint': 'Search by name, NHI, MRN, or date of birth',
  'prov.providerChart': 'Provider chart',
  'prov.identityFields': 'Identity fields',
  'prov.identityReadOnly': 'Identity fields write through PATCH /patients when the record is open.',
  'prov.editableProvider': 'Editable provider view',
  'prov.editableDetail': 'Editable patient detail',
  'prov.saveChanges': 'Save changes',
  'prov.savedLocally': 'Saved locally for the provider view',
  'prov.sampleData': 'Sample data',
  'prov.noUpdateEndpoint': 'No patient update endpoint is available yet',
  'prov.saveDisabledTitle': 'Patient updates are not available yet',
  'prov.fieldReadOnlyTitle': 'This field is read-only until patient updates are available',
  'prov.openPatientAria': 'Open {name}',
  'prov.name': 'Name',
  'prov.nameAr': 'Name (Arabic)',
  'prov.nameLatin': 'Name (Latin)',
  'prov.dob': 'Date of birth',
  'prov.dobShort': 'DOB',
  'prov.sex': 'Sex',
  'prov.phone': 'Phone',
  'prov.address': 'Address',
  'prov.nhi': 'NHI',
  'prov.verification': 'Verification',
  'prov.details': 'Details',
  'prov.suggestion': 'Suggestion',
  'prov.terminology': 'Terminology lookup',
  'prov.searchTerms': 'Search {title} terms',
  'prov.code': 'Code',
  'prov.enter': 'Enter',
  'prov.selectTermPlaceholder': 'Select a term…',
  'prov.diagnoses': 'Diagnoses',
  'prov.diagnosis': 'Diagnosis',
  'prov.medications': 'Medications',
  'prov.labs': 'Labs',
  'prov.lastVisit': 'Last visit',
  'prov.statusDose': 'Status / dose / schedule',
  'prov.statusSince': 'Status / since',
  'prov.valueUnitDate': 'Value / unit / date',
  'prov.addDiagnosis': 'Add Diagnosis',
  'prov.addLab': 'Add Lab',
  'prov.addMedication': 'Add Medication',
  'prov.authoritative': 'Authoritative',
  'prov.declared': 'Declared',
  'prov.review': 'Review',
  'prov.strong': 'Strong',
  'prov.lab': 'Lab',
  'prov.medicationKind': 'Medication',
  'prov.rosterFailed': 'The roster could not be loaded. Check the API and try again.',
  'prov.rosterEmpty': 'No patients matched your search.',
  'prov.retry': 'Retry',
  'prov.offlineSample': 'Sample data — offline demo',
  'prov.offlineNote': 'The API is unreachable — showing bundled sample data. Real data loads when the backend is reachable.',
  'prov.savedOffline': 'Saved on this device (offline). Changes will not appear for other users.',
  'prov.updatedAt': 'Updated',
  'prov.identityEditable': 'These fields write straight to the shared record through PATCH /patients. Only changed fields are sent.',
  'prov.nameRequired': 'At least one name (Arabic or Latin) is required.',
  'prov.savedToServer': 'Saved to the server record.',
  'prov.saving': 'Saving…',
  'prov.saveFailed': 'Save failed',
  'prov.patientLoadFailed': 'The patient record could not be loaded.',
  'prov.facility': 'Facility',
  'prov.mrn': 'MRN',
  'prov.status': 'Status',
  'prov.registrationSource': 'Registration source',
  'prov.med.title': 'Medications',
  'prov.med.description': 'Medication',
  'prov.med.rxnorm': 'RxNorm ingredient',
  'prov.med.dose': 'Dose',
  'prov.med.doseUnit': 'Unit (UCUM)',
  'prov.med.route': 'Route',
  'prov.med.form': 'Form',
  'prov.med.frequency': 'Frequency',
  'prov.med.reportedBy': 'Reported by',
  'prov.med.clinician': 'Clinician',
  'prov.med.patient': 'Patient',
  'prov.med.add': 'Add medication',
  'prov.med.empty': 'No medications recorded.',
  'prov.med.loadFailed': 'Medications could not be loaded.',
  'prov.med.deleteConfirm': 'Delete this medication?',
  'prov.fh.title': 'Family history',
  'prov.fh.relationship': 'Relationship',
  'prov.fh.relativeName': 'Relative name',
  'prov.fh.birthYear': 'Birth year',
  'prov.fh.condition': 'Condition (SNOMED)',
  'prov.fh.conditionRequired': 'At least one condition is required.',
  'prov.fh.add': 'Add relative',
  'prov.fh.empty': 'No family history recorded.',
  'prov.fh.loadFailed': 'Family history could not be loaded.',
  'prov.fh.deleteConfirm': 'Delete this relative record?',
  'prov.delete': 'Delete',
  'demo.allFacilities': 'All facilities',
  'demo.damascus': 'Damascus',
  'demo.mazzeh': 'Mazzeh',
  'demo.baramkeh': 'Baramkeh',
  'demo.mouwasatHospital': 'Al-Mouwasat University Hospital',
  'demo.damascusCentralLab': 'Damascus Central Lab',
  'demo.type2Diabetes': 'Type 2 diabetes mellitus',
  'demo.hypertension': 'Hypertension',
  'demo.ckdStage3': 'Chronic kidney disease stage 3',
  'demo.penicillin': 'Penicillin',
  'demo.peanut': 'Peanut',
  'demo.latex': 'Latex',
  'demo.hemoglobinA1c': 'Hemoglobin A1c',
  'demo.glucose': 'Glucose',
  'demo.totalCholesterol': 'Total Cholesterol',
  'demo.hemoglobin': 'Hemoglobin',
  'demo.systolicBp': 'Systolic Blood Pressure',
  'demo.diastolicBp': 'Diastolic Blood Pressure',
  'demo.ferritin': 'Ferritin',
  'demo.vitaminD': 'Vitamin D 25-OH',
  'demo.tsh': 'TSH',
  'demo.appendectomy': 'Appendectomy',
  'demo.appendectomyMapping': 'Appendectomy mapping',
  'demo.cardiovascular': 'Cardiovascular',
  'demo.endocrine': 'Endocrine',
  'demo.nephrology': 'Nephrology',
  'demo.respiratory': 'Respiratory',
  'demo.hematologic': 'Hematologic',
  'demo.infectiousDisease': 'Infectious disease',
  'demo.digestive': 'Digestive',
  'demo.generalSurgery': 'General Surgery',
  'demo.drug': 'Drug',
  'demo.food': 'Food',
  'demo.environmental': 'Environmental',
  'demo.hyperlipidemia': 'Hyperlipidemia',
  'demo.takeWithMeals': 'Take one tablet by mouth twice daily with meals.',
  'demo.takeAtBedtime': 'Take one tablet by mouth at bedtime.',
  'demo.takeOrallyTwice': 'I take it orally twice a day.',
  'demo.30days': '30 days',
  'demo.90days': '90 days',
  'demo.60tablets': '60 tablets',
  'demo.90tablets': '90 tablets',
  'demo.providerRoster': 'provider roster',
  'demo.oral': 'Oral',
  'demo.tablet': 'Tablet',
  'demo.bid': 'Twice daily',
  'demo.qhs': 'At bedtime',
  'demo.blood': 'Blood',
  'demo.serum': 'Serum',
  'demo.cholesterol': 'Cholesterol',
  'demo.bpSystolic': 'BP systolic',
  'demo.bpDiastolic': 'BP diastolic',
  'demo.qn': 'Quantitative',
  'demo.arterial': 'Arterial',
  'demo.calcidiol': 'Calcidiol',
  'demo.thyrotropin': 'Thyrotropin',
  'demo.myocardialInfarction': 'Myocardial infarction',
  'demo.asthma': 'Asthma',
  'demo.betaThalassemia': 'Beta-thalassemia',
  'demo.hepatitisB': 'Hepatitis B',
  'demo.ironDeficiencyAnemia': 'Iron deficiency anemia',
  'demo.appendicectomy': 'Appendicectomy',
  'demo.cabg': 'Coronary artery bypass graft',
  'demo.intravenous': 'Intravenous',
  'demo.intramuscular': 'Intramuscular',
  'demo.subcutaneous': 'Subcutaneous',
  'demo.inhalation': 'Inhalation',
  'demo.rectal': 'Rectal',
  'demo.ophthalmic': 'Ophthalmic',
  'demo.otic': 'Otic',
  'demo.transdermal': 'Transdermal',
  'demo.capsule': 'Capsule',
  'demo.solution': 'Solution',
  'demo.suspension': 'Suspension',
  'demo.cream': 'Cream',
  'demo.ointment': 'Ointment',
  'demo.patch': 'Patch',
  'demo.inhaler': 'Inhaler',
  'demo.injection': 'Injection',
  'demo.eyeDrops': 'Eye drops',
  'demo.earDrops': 'Ear drops',
  'demo.suppository': 'Suppository',
  'demo.onceDaily': 'Once daily',
  'demo.twiceDaily': 'Twice daily',
  'demo.threeTimesDaily': 'Three times daily',
  'demo.fourTimesDaily': 'Four times daily',
  'demo.atBedtime': 'At bedtime',
  'demo.asNeeded': 'As needed',
  'demo.every4Hours': 'Every 4 hours',
  'demo.every6Hours': 'Every 6 hours',
  'demo.every8Hours': 'Every 8 hours',
  'demo.onceWeekly': 'Once weekly',
  'demo.milligram': 'milligram',
  'demo.microgram': 'microgram',
  'demo.gram': 'gram',
  'demo.milliliter': 'milliliter',
  'demo.liter': 'liter',
  'demo.internationalUnit': 'international unit',
  'demo.percent': 'percent',
  'demo.puff': 'puff',
  'demo.drop': 'drop',
  'demo.unit': 'unit',
  'demo.sublingual': 'Sublingual',
  'demo.topical': 'Topical',
  'demo.in': 'ingredient',
  'demo.min': 'multi-ingredient',
  'demo.asNeededPrn': 'As needed (PRN)',
  'demo.moiExact': 'MOI ID exact match',
  'demo.moiConflict': 'MOI ID conflict',
  'demo.noMatch': 'no match',
  'demo.phoneMatch': 'phone match',
  'demo.sexMatch': 'sex match',
  'demo.latinName': 'Latin name',
  'demo.arabicName': 'Arabic name',
  'demo.dob': 'DOB',
  'demo.moderate': 'Moderate',
  'demo.completed': 'Completed',
  // form placeholders (instructive examples)
  'ph.medNarrative': 'Metformin, the white pill…',
  'ph.medHowTake': 'e.g., one tablet by mouth twice a day',
  'ph.medName': 'Metformin 500 MG Oral Tablet',
  'ph.duration': '30 days',
  'ph.quantity': '60 tablets',
  'ph.indication': 'Type 2 diabetes mellitus',
  'ph.prescriber': 'Dr. Layla Haddad',
  'ph.sigNote': 'Take one tablet by mouth twice daily with meals.',
  'ph.labsName': 'Glucose, HbA1c, hemoglobin',
  'ph.searchRxNorm': 'Search RxNorm',
  'ph.searchSnomed': 'Search SNOMED CT',
  'ph.searchIcd10': 'Search ICD-10 (WHO)',
  'ph.searchProc': 'Search OPCS-4, CCAM, or SBCS',
  'ph.allergen': 'Penicillin',
  'ph.procedure': 'Appendectomy',
  'ph.passportSample': 'e.g. N1234567',
  'ph.governorate': 'Damascus',
  'ph.city': 'Mazzeh',
  'rxn.urticaria': 'Urticaria',
  'rxn.anaphylaxis': 'Anaphylaxis',
  'rxn.rash': 'Rash',
  'rxn.angioedema': 'Angioedema',

  // explain-my-chart launcher
  'emc.badge': 'Explain my chart',
  'emc.heading': 'Ask the chart in plain language',
  'emc.desc': 'Open a read-only, conversational explanation for {name}. Answers always show cited chart sections, and Arabic switches the panel to RTL.',
  'emc.open': 'Open explainer',
  'search.exampleAr': 'أحمد محمد الحسن',
  'search.exampleLatin': 'Ahmad Al-Hassan',

  // patient search
  'search.patients': 'Search Patients',
  'search.arabicName': 'Arabic name',
  'search.latinName': 'Latin name',
  'search.dob': 'Date of birth',
  'search.searching': 'Searching…',
  'search.searchNow': 'Search',
  'search.none': 'No matching patients found.',
  'search.candidates': '{n} candidate{s} found.',
  'search.unnamed': 'Unnamed',
  'search.dobValue': 'DOB {dob} · ',
  'search.nhi': 'NHI {nhi}',
  'search.view': 'View',
  'search.strong': 'Strong match',
  'search.review2': 'Review',
  'search.weak': 'Weak',
  // registration
  'reg.title': 'Register Patient',
  'reg.sub': 'New patient · Arabic + English',
  'reg.closeAria': 'Close registration form',
  'reg.arabicBlock': 'Arabic name (الاسم بالعربية)',
  'reg.latinBlock': 'Latin name (English)',
  'reg.given': 'Given name',
  'reg.father': "Father's name",
  'reg.grandfather': "Grandfather's name",
  'reg.family': 'Family name',
  'reg.identifier': 'Identifier',
  'reg.nationalId': 'National ID',
  'reg.passport': 'Passport',
  'reg.nationalIdField': 'National ID (11 digits)',
  'reg.passportField': 'Passport number',
  'reg.dob': 'Date of birth',
  'reg.sex': 'Sex',
  'reg.male': 'Male',
  'reg.female': 'Female',
  'reg.phone': 'Phone (+963)',
  'reg.governorate': 'Governorate',
  'reg.city': 'City',
  'reg.registering': 'Registering…',
  'reg.submit': 'Register Patient',
  'reg.cancel': 'Cancel',
  // patient banner
  'banner.verifiedAria': 'Verified identity',
  'banner.verifiedTitle': 'Identity verified — Level 4',
  'banner.copyNhiAria': 'Copy NHI to clipboard',
  'banner.level': 'Level {n}: {name}',
  'banner.noteBtn': 'Note',
  'banner.notesBtn': 'Notes',
  'banner.allergies': 'Allergies',
  'banner.noAllergies': 'No known allergies',
  'banner.viewAllergy': 'View {name} in Allergies',
  'banner.directives': 'Advance Directives',
  'banner.viewDirective': 'View {type} advance directive',
  'banner.activeOk': 'Active',
  'banner.draft': 'Draft',
  'banner.noneOnFile': 'None on file',
  'banner.latestNote': 'Latest Note',
  'banner.viewAll': 'View all ▸',
  'banner.addNote': 'Add Clinical Note',
  'banner.close': 'Close',
  'banner.notePlaceholder': 'Type a clinical note...',
  'banner.saveNote': 'Save Note',
  // patient profile dialog
  'profile.title': 'Patient Profile',
  'profile.closeAria': 'Close profile',
  'profile.unnamed': 'Unnamed',
  'profile.nhi': 'NHI {nhi}',
  'profile.dob': 'Date of birth',
  'profile.sex': 'Sex',
  'profile.phone': 'Phone',
  'profile.mrn': 'MRN',
  'profile.facility': 'Facility',
  'profile.status': 'Status',
  'profile.tier': 'Verification tier: Level {n} — {name}',
  // portable proof / encounters / documents
  'proof.aria': 'Portable proof of vaccination',
  'proof.title': 'Vaccine proof',
  'proof.std': 'SMART Health Card · SMART Health Link',
  'proof.shc': 'SMART Health Card',
  'proof.shcDesc': "COVID-19 vaccination proof. Offline-verifiable — scan to confirm the issuer's signature without a server lookup.",
  'proof.shcAria': 'Open COVID-19 SMART Health Card QR',
  'proof.shcImg': 'COVID-19 SMART Health Card QR code',
  'proof.viewQr': 'View QR',
  'proof.shl': 'SMART Health Link',
  'proof.shlDesc': 'Full vaccine record behind an encrypted, passcode-protected link (48h expiry).',
  'proof.shlAria': 'Open SMART Health Link QR',
  'proof.shlImg': 'SMART Health Link QR code',
  'proof.close': 'Close',
  'proof.shareLink': 'Share link',
  'proof.copyLinkAria': 'Copy share link',
  'proof.shcModalHint': "Scan to verify the COVID-19 vaccination record offline. The card is signed by the issuer (ES256) and carries only the patient's birth date + per-dose vaccine facts.",
  'proof.shlModalHint': 'Passcode {code} (dev/seed only). Link expires 48h after creation.',
  'proof.encountersAria': 'Recent encounters',
  'proof.encounters': 'Recent encounters',
  'proof.nVisits': '{n} visits',
  'proof.documentsAria': 'Documents',
  'proof.documents': 'Documents',
  'proof.nFiles': '{n} files',
  'proof.pdf': 'PDF',
  'proof.image': 'Image',
  'enc.emergency': 'Emergency',
  'enc.outpatient': 'Outpatient',
  'enc.inpatient': 'Inpatient',
  'enc.telehealth': 'Telehealth',
  'enc.home': 'Home',
  'enc.refer': 'Referred',
  'enc.admitted': 'Admitted',
  'enc.reason.chest': 'Chest pain, rule out ACS',
  'enc.loc.mouwasat_er': 'Al-Mouwasat University Hospital · ER',
  'enc.loc.mouwasat_endo': 'Al-Mouwasat University Hospital · Endocrine',
  'enc.loc.telehealth': 'Telehealth',
  'enc.reason.diabetes': 'Diabetes follow-up (HbA1c 7.4%)',
  'enc.reason.adherence': 'Medication adherence review',
  'doc.discharge-summary': 'Discharge summary',
  'doc.lab-report': 'Lab report',
  'doc.imaging-report': 'Imaging report',
  'doc.referral-letter': 'Referral letter',
  'doc.scanned-paper': 'Scanned paper',
  'doc.consent': 'Consent',
  'doc.insurance': 'Insurance',
  'doc.other': 'Other',
  'doc.title.discharge': 'Discharge summary — ER visit (2026-08-27)',
  'doc.title.lab': 'Scanned lab report — CBC + HbA1c',
  'doc.title.xray': 'Chest X-ray report',

  // login page
  'login.title': 'SyriaPHR',
  'login.subtitle': 'National Personal Health Record',
  'login.rolePrompt': 'Select your role',
  'login.role.patient': 'Patient',
  'login.role.patientDesc': 'Access your personal health record',
  'login.role.provider': 'Provider',
  'login.role.providerDesc': 'Clinical workspace for healthcare professionals',
  'login.username': 'Username',
  'login.usernamePlaceholder': 'Enter username',
  'login.password': 'Password',
  'login.passwordPlaceholder': 'Enter password',
  'login.submit': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.error': 'Invalid username or password',
  'login.langAria': 'Switch language',
  'login.footer': 'Demo build — use admin / admin',
  'login.noNhi': "Don't have an NHI?",
  'login.registerNhi': 'Register now',

  // immunizations panel (patient view)
  'imm.title': 'Immunizations',
  'imm.aria': 'Immunizations',
  'imm.loading': 'Loading immunizations…',
  'imm.empty': 'No immunization records yet.',
  'imm.doseOf': 'Dose {n}',
  'imm.cvxTitle': 'CDC CVX vaccine code {code}',
  'imm.selfReported': 'self-reported',
  'imm.status.complete': 'Complete',
  'imm.status.partial': 'Partial',
  'imm.status.notDone': 'Not done',
  'imm.status.error': 'Entered in error',
  'imm.status.recorded': 'Recorded',
  'imm.recommendations': 'Recommendations',
  'imm.caveat':
    'Based on the WHO reference schedule — your clinician confirms all doses. This is not yet the confirmed Syrian MoH/EPI national schedule.',
  'imm.dueNow': 'Due now',
  'imm.dueOn': 'Due',
  'imm.notYetEligible': 'Not yet eligible until',
  'imm.seriesComplete': 'Series complete',
  'imm.boosterNote': 'Booster',
  // devices panel (patient view)
  'dev.title': 'Devices',
  'dev.aria': 'Devices — implants and clinical hardware',
  'dev.loading': 'Loading devices…',
  'dev.empty': 'No devices recorded.',
  'dev.implantedAria': 'Implanted device — may affect MRI, surgery, or emergency care',
  'dev.implantedOn': 'Implanted',
  'dev.removedOn': 'removed',
  'dev.detailsAria': 'Toggle device details',
  'dev.field.udi': 'UDI-DI',
  'dev.field.serial': 'Serial / Lot',
  'dev.field.model': 'Device name / model',
  'dev.field.manufacturer': 'Manufacturer',
  'dev.field.site': 'Body site',
  'dev.field.indication': 'Indication',
  'dev.field.notes': 'Notes',
  'dev.mri.safe': 'MR Safe',
  'dev.mri.conditional': 'MR Conditional',
  'dev.mri.unsafe': 'MR Unsafe',
  'dev.mri.unknown': 'MRI unknown',
  'dev.status.active': 'Active',
  'dev.status.removed': 'Removed',
  'dev.status.replaced': 'Replaced',
  'dev.status.inactive': 'Inactive',
}

const ar: Dict = {
  // shell
  'app.tagline': 'السجل الصحي الشخصي الوطني',
  'shell.patient': 'المريض',
  'shell.provider': 'مقدّم الرعاية',
  'shell.toggleLangAria': 'تبديل اللغة',
  'shell.arabicLabel': 'عربي',
  'shell.englishLabel': 'English',
  'shell.switchToArabic': 'تبديل الواجهة إلى العربية',
  'shell.switchToEnglish': 'تبديل الواجهة إلى الإنجليزية',
  'shell.rosterActive': 'لوحة مقدّم الرعاية نشطة',
  'shell.verifiedNhi': 'NHI موثَّق: SYR-2026-000001',
  'shell.homeAria': 'الصفحة الرئيسية — سوريا PHR',
  'shell.primaryNav': 'التنقل الرئيسي',
  'nav.home': 'الرئيسية',
  'nav.register': 'تسجيل مريض',
  'nav.search': 'بحث',
  'nav.roster': 'قائمة المرضى',
  'nav.nhiRegister': 'تسجيل الرقم الصحي',
  'nav.myChart': 'سجلّي الصحي',
  'nav.myNHI': 'رقمي الصحي',
  'nav.patientMode': 'وضع المريض',
  // domains
  'domain.labs.title': 'المختبر',
  'domain.labs.subtitle': 'الطلبات والنتائج موحّدة وفق نظام LOINC.',
  'domain.meds.title': 'الأدوية',
  'domain.meds.subtitle': 'سجلات دوائية وفق RxNorm جاهزة للربط على مستوى المادة الفعّالة.',
  'domain.diagnoses.title': 'التشخيصات',
  'domain.diagnoses.subtitle': 'سجلات مشفّرة وفق التصنيف الدولي للأمراض ICD-10 (النسخة الدولية لمنظمة الصحة العالمية).',
  'domain.allergies.title': 'الحساسية',
  'domain.allergies.subtitle': 'سجلات الحساسية والتفاعلات الدوائية الضارة وفق SNOMED CT.',
  'domain.procedures.title': 'الإجراءات',
  'domain.procedures.subtitle': 'سجلات الإجراءات وفق OPCS-4 وCCAM ومرشّحي SBCS.',
  // domain pages
  'domain.addEntry': 'إضافة سجل',
  'domain.newEntry': 'سجل {title} جديد',
  'domain.closeForm': 'إغلاق النموذج',
  'domain.saveEntry': 'حفظ السجل',
  'domain.cancel': 'إلغاء',
  'domain.patientRecord': 'سجل المريض',
  'domain.lookupTable': 'جدول المراجع',
  'domain.searchPlaceholder': 'ابحث برمز أو مصطلح أو نظام',
  'domain.searchLookupAria': 'البحث في جدول المراجع',
  'domain.clearSearch': 'مسح البحث',
  'domain.searching': 'جارٍ البحث…',
  'domain.resultsFor': 'نتائج البحث عن «{q}»',
  'domain.allTerms': 'كل المصطلحات',
  'domain.nResults': '{n} نتيجة',
  'domain.nResultsPlural': '{n} نتائج',
  'domain.use': 'استخدام',
  'domain.useTermAria': 'استخدام المصطلح',
  'domain.useToPrefillAria': 'استخدام {name} لتعبئة النموذج',
  'domain.clickToPrefill': 'انقر لتعبئة النموذج',
  'domain.noMatchingTerms': 'لا توجد مصطلحات مطابقة — امسح البحث لعرض القائمة كاملة.',
  'domain.nEntries': '{n} سجل',
  'domain.nEntriesPlural': '{n} سجلات',
  'domain.noEntries': 'لا توجد سجلات بعد — استخدم «إضافة سجل» أو انقر على صف في جدول المراجع لإنشاء سجل.',
  'domain.actions': 'الإجراءات',
  'domain.deleteAria': 'حذف {name}',
  'domain.entrySaved': 'تم حفظ سجل {title} في ملف المريض.',
  'domain.entryWord': 'السجل',
  'domain.addedToForm': 'أُضيف إلى النموذج',
  'domain.selectPlaceholder': 'اختر…',
  'domain.searchCode': 'ابحث أو أدخل الرمز',
  'domain.enterName': 'أدخل الاسم',
  'domain.codeShort': 'الرمز',
  'domain.codeNameRequired': 'الرمز والاسم مطلوبان لكل سجل.',
  'domain.noFutureDates': 'لا يمكن أن تكون التواريخ في المستقبل.',
  'domain.medDescRequired': 'وصف الدواء مطلوب لكل سجل.',
  'domain.clinicianSigRequired': 'السجلات الموصوفة من الطبيب تتطلب حقل وصف واحداً على الأقل (الجرعة، طريقة الإعطاء، الشكل، أو التكرار).',
  // generic field labels
  'field.code': 'الرمز',
  'field.name': 'الاسم',
  'field.status': 'الحالة',
  'field.value': 'القيمة',
  'field.unit': 'الوحدة',
  'field.date': 'التاريخ',
  'field.range': 'المدى المرجعي',
  'field.since': 'منذ',
  'field.system': 'النظام',
  'field.component': 'المكوّن',
  'field.scale': 'المقياس',
  'field.test': 'الفحص',
  'field.observation': 'القياس',
  'field.preferredTerm': 'المصطلح المفضّل',
  'field.category': 'التصنيف',
  'field.condition': 'الحالة المرضية',
  'field.allergen': 'مادة الحساسية',
  'field.reaction': 'التفاعل',
  'field.severity': 'درجة الخطورة',
  'field.procedure': 'الإجراء',
  'field.provenance': 'المصدر',
  'field.note': 'ملاحظة',
  'field.medication': 'الدواء',
  'field.howTake': 'كيف تتناول دواءك؟',
  'field.dose': 'الجرعة',
  'field.doseUnit': 'وحدة الجرعة',
  'field.route': 'طريقة الإعطاء',
  'field.form': 'الشكل الصيدلاني',
  'field.frequency': 'التكرار',
  'field.duration': 'المدة',
  'field.quantity': 'الكمية',
  'field.refills': 'مرات إعادة الصرف',
  'field.indication': 'الاستطباب',
  'field.prescriber': 'الطبيب الواصف',
  'field.sigNote': 'ملاحظة الوصف الطبي',
  'field.provenanceToggle': 'مصدر الإبلاغ',
  'field.patientReported': 'من إبلاغ المريض',
  'field.prescribed': 'موصوف طبياً',
  'sig.qty': 'الكمية',
  'sig.refill': 'إعادة صرف',
  'sig.refills': 'إعادة صرف',
  // status vocabulary
  'status.active': 'نشط',
  'status.paused': 'موقوف مؤقتاً',
  'status.completed': 'مكتمل',
  'status.stopped': 'موقوف',
  'status.resolved': 'منتهٍ',
  'status.historic': 'سابق',
  'status.inactive': 'غير نشط',
  'status.enteredInError': 'أُدخل بالخطأ',
  'status.mild': 'خفيف',
  'status.moderate': 'متوسط',
  'status.severe': 'شديد',
  'status.lifeThreatening': 'يهدّد الحياة',
  'status.planned': 'مخطط',
  'status.cancelled': 'ملغى',
  'status.draft': 'مسودة',
  // dashboard
  'dash.identity': 'التحقق من الهوية',
  'dash.tierModel': 'نموذج ضمان من أربع مستويات',
  'dash.tiersAria': 'مستويات التحقق',
  'dash.recentActivity': 'النشاط الأخير',
  'dash.timelineAria': 'الخط الزمني الصحي الأخير',
  'dash.scrollMore': 'مرّر لعرض المزيد',
  'dash.vitalsAria': 'اتجاهات العلامات الحيوية',
  'dash.domainsAria': 'مجالات السجل',
  'dash.systolicBp': 'ضغط الدم الانقباضي',
  'dash.diastolicBp': 'ضغط الدم الانبساطي',
  'dash.weight': 'الوزن',
  'dash.hba1c': 'السكر التراكمي (HbA1c)',
  'dash.trendVsLast': '{delta} عن آخر زيارة',
  'dash.trendThisMonth': '{delta} هذا الشهر',
  'dash.trendAria': 'اتجاه {label} عبر الزيارات الأخيرة',
  'dash.demoNote': 'متابعة السكر التراكمي (7.4%). نوقشت التعديلات الغذائية والالتزام بتناول الميتفورمين. المريض متعاون؛ خطة لإعادة فحص السكر التراكمي بعد 3 أشهر ومراجعة ضبط ضغط الدم في الزيارة القادمة.',
  'dash.directive.livingWillSummary': 'توثيق رغبة التبرع بالأعضاء',
  'dash.directive.dnrSummary': 'بانتظار اجتماع العائلة',
  'dash.toastNoteSoon': 'محرّر الملاحظات قريبا — الواجهة الخلفية غير مربوطة بعد.',
  'dash.toastNotesSoon': 'عرض الملاحظات السريرية الكاملة قريبا.',
  'dash.documentsProof': 'المستندات والإثباتات',
  'dash.documentsProofSummary': 'بطاقات رمز التطعيم (بطاقة الصحة الذكية · رابط الصحة الذكية) وإثبات التطعيم القابل للمشاركة.',
  'dash.encDocs': 'الزيارات والمستندات',
  'dash.encDocsSummary': 'سجل الزيارات والمستندات المحفوظة',
  'dash.identityDetails': 'تفاصيل الهوية',
  'dash.showMore': 'عرض المزيد',
  'dash.showLess': 'عرض أقل',
  'dash.lastUpdated': 'آخر تحديث {date}',
  'dash.clinicalSectionsAria': 'أقسام السجل السريري',
  'dash.assuranceModel': 'كيف تعمل مستويات ضمان الهوية',
  'dash.openDomain': 'فتح {title}',
  'tl.hba1c.title': 'نتيجة السكر التراكمي',
  'tl.hba1c.detail': '7.4% · مختبر دمشق المركزي',
  'tl.hba1c.time': 'قبل ساعتين',
  'tl.metformin.title': 'إعادة صرف ميتفورمين',
  'tl.metformin.detail': '500 مغ · 60 قرصاً',
  'tl.metformin.time': 'قبل يوم',
  'tl.diagnosis.title': 'تحديث تشخيص',
  'tl.diagnosis.detail': 'مرض كلوي مزمن — المرحلة 3 · الحالة: نشط',
  'tl.diagnosis.time': 'قبل 3 أيام',
  'tl.procedure.title': 'تسجيل إجراء',
  'tl.procedure.detail': 'استئصال الزائدة الدودية · OPCS-4 H22.9',
  'tl.procedure.time': 'قبل أسبوع',
  'tl.allergy.title': 'مراجعة حساسية',
  'tl.allergy.detail': 'البنسلين · درجة الخطورة: متوسطة',
  'tl.allergy.time': 'قبل أسبوعين',
  'tl.checkup.title': 'فحص سنوي',
  'tl.checkup.detail': 'طب عام · مستشفى المواسات',
  'tl.checkup.time': 'قبل شهر',
  'tier.desc1': 'الهوية مُعلنة ذاتياً عند التسجيل، دون إثبات مستقل حتى الآن.',
  'tier.desc2': 'مطابقة مع سجلات وزارة الداخلية بنتيجة ثقة أولية.',
  'tier.desc3': 'تمرير بطاقة وزارة الداخلية أو تحقق يدوي من موظف مسجّل.',
  'tier.desc4': 'أعلى مستوى ضمان — تأكيد بيومتري أو من مصدر رسمي معتمد.',
  // portal
  'portal.eyebrow': 'السجل الأدنى · المرحلة 2',
  'portal.registerTitle': 'تسجيل مريض',
  'portal.searchTitle': 'البحث عن مرضى',
  'portal.registerDesc': 'إنشاء سجل مريض جديد باسم عربي ولاتيني منظّم ومعرّف رسمي.',
  'portal.searchDesc': 'البحث عن مريض مسجّل بالاسم وتاريخ الميلاد، مع درجات مطابقة الفهرس الرئيسي للمرضى.',
  'portal.register': 'تسجيل',
  'portal.search': 'بحث',
  'portal.registeredOk': 'تم تسجيل المريض — NHI {nhi} ({action})',
  'portal.action.createdNew': 'إنشاء سجل جديد',
  'portal.action.linkedExisting': 'ربط مع سجل موجود',
  'reg.subtitle': 'مريض جديد · عربي + لاتيني · المرحلة 2',
  'reg.arabicName': 'الاسم بالعربية',
  'reg.latinName': 'الاسم بالحروف اللاتينية (إنكليزي)',
  'reg.nationalIdLabel': 'رقم الهوية الشخصية (11 رقماً)',
  'reg.passportLabel': 'رقم جواز السفر',
  'reg.select': 'اختر…',
  'reg.cityPlaceholder': 'المزة',
  'reg.submitting': 'جارٍ التسجيل…',
  'reg.nameRequired': 'أدخل الاسم بالعربية و/أو بالحروف اللاتينية.',
  'reg.nidDigits': 'يجب أن يتألف رقم الهوية الشخصية من 11 رقماً.',
  'reg.failed': 'فشل التسجيل. يرجى المحاولة مجدداً.',
  'search.nameAr': 'الاسم بالعربية',
  'search.nameLatin': 'الاسم باللاتينية',
  'search.submit': 'بحث',
  'search.needInput': 'أدخل الاسم و/أو تاريخ الميلاد للبحث.',
  'search.failed': 'فشل البحث.',
  'search.candidatesPlural': 'تم العثور على {n} مرشّحين.',
  'search.review': 'للمراجعة',
  'search.dobPrefix': 'تاريخ الميلاد: ',
  'profile.failed': 'تعذّر تحميل بيانات المريض.',
  'profile.tierLine': 'مستوى التحقق: المستوى {n} — {name}',
  // banner
  'banner.photoAlt': 'صورة المريض — أحمد الحسن',
  'banner.verifiedBadge': 'الهوية موثّقة — المستوى 4',
  'banner.male': 'ذكر',
  'banner.female': 'أنثى',
  'banner.copyNhi': 'نسخ المعرّف الوطني إلى الحافظة',
  'banner.levelShort': 'المستوى {n}: {name}',
  'banner.note': 'ملاحظة',
  'banner.notes': 'الملاحظات',
  'banner.viewAllergyAria': 'عرض {name} في صفحة الحساسية',
  'banner.reviewBeforePrescribing': '⚠️ راجِع قبل الوصف الطبي',
  'banner.allergyType.drug': 'دواء',
  'banner.allergyType.food': 'طعام',
  'banner.allergyType.environmental': 'بيئي',
  'banner.vitals': 'العلامات الحيوية',
  'banner.moreVitals': '+{n} أخرى',
  'banner.directiveType.living-will': 'الوصية الطبية المسبقة',
  'banner.directiveType.dnr-discussion': 'مناقشة عدم الإنعاش',
  'banner.activeBadge': '✓ نشط',
  'banner.draftBadge': '… مسودة',
  'banner.manage': 'إدارة',
  'banner.cancel': 'إلغاء',
  'banner.ageYears': '{n} عامًا',
  'vital.height': 'الطول',
  'vital.weight': 'الوزن',
  'vital.bmi': 'كتلة الجسم',
  'vital.bp': 'ضغط الدم',
  'vital.hba1c': 'السكر التراكمي',

  // Add-vitals panel (patient-entered vitals with SNOMED mapping)
  'vitals.title': 'سجلّ العلامات الحيوية',
  'vitals.aria': 'سجلّ العلامات الحيوية',
  'vitals.nReadings': '{n} قراءة',
  'vitals.loading': 'جارٍ تحميل العلامات الحيوية…',
  'vitals.loadFailed': 'تعذّر تحميل العلامات الحيوية. تحقّق من الواجهة البرمجية وحاول مجددًا.',
  'vitals.empty': 'لا توجد علامات حيوية مسجّلة بعد.',
  'vitals.addVital': 'إضافة علامة حيوية',
  'vitals.closeForm': 'إغلاق النموذج',
  'vitals.formTitle': 'تسجيل علامة حيوية',
  'vitals.formHint': 'القراءات المُدخَلة ذاتيًا تُوسَم بأنها مُبلَّغ عنها من المريض.',
  'vitals.type': 'نوع العلامة الحيوية',
  'vitals.type.blood_pressure_systolic': 'ضغط الدم — الانقباضي',
  'vitals.type.blood_pressure_diastolic': 'ضغط الدم — الانبساطي',
  'vitals.type.height': 'الطول',
  'vitals.type.weight': 'الوزن',
  'vitals.type.o2_saturation': 'تشبّع الأكسجين (SpO₂)',
  'vitals.type.pain_level': 'شدّة الألم',
  'vitals.bloodPressurePair': 'ضغط الدم (الانقباضي + الانبساطي)',
  'vitals.value': 'القيمة',
  'vitals.valueSystolic': 'الانقباضي',
  'vitals.valueDiastolic': 'الانبساطي',
  'vitals.unit': 'الوحدة',
  'vitals.when': 'التاريخ والوقت',
  'vitals.whenHint': 'يُضبط تلقائيًا على الوقت الحالي — عدِّله فقط إذا أُخذت القراءة سابقًا.',
  'vitals.submit': 'حفظ العلامة الحيوية',
  'vitals.submitting': 'جارٍ الحفظ…',
  'vitals.cancel': 'إلغاء',
  'vitals.saved': 'تم تسجيل العلامة الحيوية.',
  'vitals.saveFailed': 'تعذّر حفظ العلامة الحيوية.',
  'vitals.err.required': 'أدخل قيمة.',
  'vitals.err.number': 'أدخل قيمة رقمية.',
  'vitals.err.range': 'يجب أن تكون القيمة بين {min} و {max} {unit}.',
  'vitals.err.pair': 'أدخل القيمتين الانقباضية والانبساطية.',
  'vitals.err.datetime': 'أدخل تاريخًا ووقتًا صالحَين.',
  'vitals.err.o2Range': 'يجب أن يكون تشبّع الأكسجين بين 0 و 100%.',
  'vitals.err.painRange': 'يجب أن تكون شدّة الألم بين 0 و 10.',
  'vitals.col.vital': 'العلامة',
  'vitals.col.value': 'القيمة',
  'vitals.col.snomed': 'سنوميد',
  'vitals.col.when': 'وقت التسجيل',
  'vitals.col.source': 'المصدر',
  'vitals.source.patient': 'المريض',
  'vitals.source.clinician': 'الطبيب',
  'vitals.source.import': 'مستورَد',
  'vitals.snomedAria': 'رمز سنوميد CT {code}',
  // portable proof + encounters + documents
  'proof.subtitle': 'بطاقة الصحة الذكية · رابط الصحة الذكي',
  'proof.shcTitle': 'بطاقة الصحة الذكية',
  'proof.shcAlt': 'رمز QR لبطاقة الصحة الذكية لكوفيد-19',
  'proof.shlTitle': 'رابط الصحة الذكي',
  'proof.shlAlt': 'رمز QR لرابط الصحة الذكي',
  'proof.modalShc': 'بطاقة الصحة الذكية لكوفيد-19',
  'proof.modalShl': 'رابط الصحة الذكي',
  'proof.shcModalDesc': 'امسح الرمز للتحقق من سجل التطعيم ضد كوفيد-19 دون اتصال. البطاقة موقّعة من الجهة المصدِّرة (ES256) وتحمل تاريخ ميلاد المريض وحققات الجرعات فقط.',
  'proof.passcodeNote': 'رمز المرور {code} (وضع التطوير فقط). ينتهي الرابط بعد 48 ساعة من إنشائه.',
  'enc.title': 'الزيارات الأخيرة',
  'enc.aria': 'الزيارات الأخيرة',
  'enc.nVisits': '{n} زيارات',
  'enc.type.emergency': 'طارئة',
  'enc.type.outpatient': 'عيادة خارجية',
  'enc.type.inpatient': 'إقامة',
  'enc.type.telehealth': 'عن بُعد',
  'enc.type.home': 'منزلية',
  'enc.reason.enc-1': 'ألم في الصدر — استبعاد متلازمة الشريان التاجي الحادة',
  'enc.location.enc-1': 'مستشفى المواسات الجامعي · قسم الطوارئ',
  'enc.reason.enc-2': 'متابعة السكري (HbA1c 7.4%)',
  'enc.location.enc-2': 'مستشفى المواسات الجامعي · الغدد الصماء',
  'enc.reason.enc-3': 'مراجعة الالتزام بالدواء',
  'enc.location.enc-3': 'استشارة عن بُعد',
  'enc.disposition.home': 'إلى المنزل',
  'doc.title': 'المستندات',
  'doc.aria': 'المستندات',
  'doc.nFiles': '{n} ملفات',
  'doc.type.discharge-summary': 'ملخص خروج',
  'doc.type.lab-report': 'تقرير مخبري',
  'doc.type.imaging-report': 'تقرير تصوير',
  'doc.type.referral-letter': 'رسالة إحالة',
  'doc.type.scanned-paper': 'ورقة ممسوحة',
  'doc.type.consent': 'موافقة',
  'doc.type.insurance': 'تأمين',
  'doc.type.other': 'أخرى',
  'doc.title.doc-1': 'ملخص الخروج — زيارة الطوارئ (2026-08-27)',
  'doc.title.doc-2': 'تقرير مخبري ممسوح — CBC + HbA1c',
  'doc.title.doc-3': 'تقرير أشعة سينية على الصدر',
  'doc.pdf': 'PDF',
  'doc.image': 'صورة',
  // لوحة تفاصيل الزيارة والحالات (عرض متوافق مع FHIR، ADR-015)
  'enc.loading': 'جارٍ تحميل الزيارات…',
  'enc.loadFailed': 'تعذّر تحميل الزيارات. تحقّق من الواجهة البرمجية وحاول مجدداً.',
  'enc.empty': 'لا توجد زيارات مسجّلة بعد.',
  'enc.openAria': 'فتح الزيارة: {reason}',
  'enc.icd10ChipAria': 'رمز ICD-10-WHO (الإصدار الدولي لمنظمة الصحة العالمية)',
  'enc.panelTitle': 'الزيارة',
  'enc.field.type': 'النوع',
  'enc.field.status': 'الحالة',
  'enc.field.period': 'الفترة',
  'enc.field.reason': 'السبب',
  'enc.field.practitioner': 'الممارِس',
  'enc.field.location': 'الموقع',
  'enc.field.disposition': 'حالة الخروج',
  'enc.notesHeading': 'الملاحظات السريرية',
  'enc.noNotes': 'لا توجد ملاحظات مسجّلة على هذه الزيارة.',
  'enc.status.planned': 'مخطَّطة',
  'enc.status.in-progress': 'جارية',
  'enc.status.finished': 'منتهية',
  'enc.status.cancelled': 'ملغاة',
  'enc.status.entered-in-error': 'أُدخلت خطأً',
  'enc.noteType.progress': 'ملاحظة تقدّم',
  'enc.noteType.admission': 'ملاحظة قبول',
  'enc.noteType.discharge-summary': 'ملخص خروج',
  'enc.noteType.op-note': 'ملاحظة عملية',
  'enc.noteType.nursing': 'ملاحظة تمريض',
  'enc.noteType.phone': 'ملاحظة هاتفية',
  'enc.noteType.free-text': 'ملاحظة حرة',
  'enc.noteSource.clinician': 'الطبيب',
  'enc.noteSource.patient': 'المريض',
  'enc.noteSource.import': 'مستورَدة',
  'enc.signed.draft': 'مسودة',
  'enc.signed.signed': 'موقّعة',
  'enc.signed.amended': 'معدَّلة',
  // AI-Scribe "Speak encounter" dock
  'scribe.title': 'إملاء المقابلة السريرية',
  'scribe.newEncounter': 'مقابلة جديدة',
  'scribe.close': 'إغلاق',
  'scribe.encounterType': 'نوع المقابلة',
  'scribe.facility': 'المنشأة',
  'scribe.periodStart': 'وقت البدء',
  'scribe.reason': 'سبب الزيارة',
  'scribe.reasonHint': 'اختياري — مثال: متابعة السكري',
  'scribe.startRecording': 'بدء التسجيل',
  'scribe.stopRecording': 'إيقاف التسجيل',
  'scribe.recording': 'جارٍ التسجيل…',
  'scribe.transcribing': 'جارٍ تفريغ الملاحظة وبنيتها…',
  'scribe.transcribingHint': 'يستغرق هذا عادةً من 10 إلى 60 ثانية.',
  'scribe.transcript': 'النص المفرَّغ',
  'scribe.noteLabel': 'مسودة الملاحظة (عدِّلها قبل الاعتماد)',
  'scribe.suggestions': 'مصطلحات مقترحة',
  'scribe.commit': 'اعتماد المقابلة',
  'scribe.committing': 'جارٍ الاعتماد…',
  'scribe.reviewHint': 'راجِع الملاحظة وعدِّلها — أنت من يوقّعها بصفته الطبيب.',
  'scribe.committed': 'حُفظت المقابلة في السجل.',
  'scribe.committedHint': 'أصبحت المقابلة مدرجة ضمن المقابلات الأخيرة.',
  'scribe.viewEncounter': 'عرض المقابلة',
  'scribe.toastCommitted': 'اعتُمدت المقابلة المنطوقة في السجل.',
  'scribe.micError': 'الميكروفون غير متاح — تحقّق من أذونات المتصفح وحاول مجددًا.',
  'scribe.failed': 'فشلت خطوة الإملاء أثناء {at}. يبقى الصوت محفوظًا محليًا حتى تعيد المحاولة.',
  'scribe.stage.transcribing': 'التفريغ',
  'scribe.stage.committing': 'الاعتماد',
  'scribe.retry': 'إعادة المحاولة',
  // لوحة تفاصيل المستند وحالاته (عرض DocumentReference)
  'doc.loading': 'جارٍ تحميل المستندات…',
  'doc.loadFailed': 'تعذّر تحميل المستندات. تحقّق من الواجهة البرمجية وحاول مجدداً.',
  'doc.empty': 'لا توجد مستندات مسجّلة بعد.',
  'doc.openAria': 'فتح المستند: {title}',
  'doc.panelTitle': 'المستند',
  'doc.text': 'نص',
  'doc.field.filename': 'اسم الملف',
  'doc.field.mime': 'النوع (MIME)',
  'doc.field.size': 'الحجم',
  'doc.field.sha256': 'بصمة SHA-256',
  'doc.field.uploadedBy': 'رُفع بواسطة',
  'doc.field.uploadedAt': 'تاريخ الرفع',
  'doc.field.retention': 'فئة الاحتفاظ',
  'doc.field.encounter': 'الزيارة المرتبطة',
  'doc.field.contentStatus': 'حالة المحتوى',
  'doc.noEncounter': 'غير مرتبط بزيارة',
  'doc.sizeB': '{n} بايت',
  'doc.sizeKB': '{n} كيلوبايت',
  'doc.sizeMB': '{n} ميغابايت',
  'doc.retention.legal': 'قانونية',
  'doc.retention.clinical': 'سريرية',
  'doc.retention.administrative': 'إدارية',
  'doc.contentStatus.available': 'متاح',
  'doc.contentStatus.missing': 'مفقود',
  'doc.contentStatus.expired': 'منتهي الصلاحية',
  'doc.bodyFollowup': 'سيُفتح محتوى المستند من طبقة التخزين في إصدار لاحق؛ تعرض هذه اللوحة السجل الوصفي الكامل.',
  'panel.close': 'إغلاق اللوحة',
  'panel.loading': 'جارٍ تحميل التفاصيل…',
  'panel.loadFailed': 'تعذّر تحميل التفاصيل. أغلق وحاول مجدداً.',
  'tier.name1': 'مُعلن ذاتياً',
  'tier.name2': 'موثّق — أولي',
  'tier.name3': 'موثّق — قوي',
  'tier.name4': 'رسمي معتمد',
  'tier.short1': 'مُعلن ذاتياً',
  'tier.short2': 'توثيق أولي',
  'tier.short3': 'توثيق قوي',
  'tier.short4': 'رسمي معتمد',
  'explain.launcher': 'اشرح لي ملفي',
  'explain.launcherDesc': 'افتح شرحاً حوارياً للقراءة فقط لملف {patientName}. تعرض الإجابات دائماً أقسام السجل الموثّقة بالمصادر، وتحوّل العربية اللوحة إلى اتجاه من اليمين إلى اليسار.',
  'prov.roster': 'قائمة المرضى',
  'prov.allPatients': 'كل المرضى',
  'prov.rosterDesc': 'ابحث في السجل وافتح أي مريض. يعرض هذا النموذج جميع المرضى من دون حصر على منشأة محددة.',
  'prov.backToRoster': 'العودة إلى القائمة',
  'prov.patientList': 'قائمة المرضى',
  'prov.loadingRoster': 'جارٍ تحميل السجل…',
  'prov.onePatient': '{n} مريض',
  'prov.manyPatients': '{n} مرضى',
  'prov.searchRoster': 'البحث في القائمة',
  'prov.searchAll': 'البحث في جميع المرضى',
  'prov.searchHint': 'ابحث بالاسم أو NHI أو MRN أو تاريخ الميلاد',
  'prov.providerChart': 'ملف مقدّم الرعاية',
  'prov.identityFields': 'بيانات الهوية',
  'prov.identityReadOnly': 'تُكتب بيانات الهوية عبر PATCH ‏/patients عند فتح السجل.',
  'prov.editableProvider': 'عرض قابل للتعديل — مقدّم الرعاية',
  'prov.editableDetail': 'تفصيل قابل للتعديل',
  'prov.saveChanges': 'حفظ التعديلات',
  'prov.savedLocally': 'حُفظ محلياً في عرض مقدّم الرعاية',
  'prov.sampleData': 'بيانات نموذجية',
  'prov.noUpdateEndpoint': 'لا توجد واجهة لتحديث بيانات المريض بعد',
  'prov.saveDisabledTitle': 'تحديث بيانات المريض غير متاح بعد',
  'prov.fieldReadOnlyTitle': 'هذا الحقل للقراءة فقط إلى أن يتوفر تحديث بيانات المريض',
  'prov.openPatientAria': 'فتح {name}',
  'prov.name': 'الاسم',
  'prov.nameAr': 'الاسم (عربي)',
  'prov.nameLatin': 'الاسم (لاتيني)',
  'prov.dob': 'تاريخ الميلاد',
  'prov.dobShort': 'تاريخ الميلاد',
  'prov.sex': 'الجنس',
  'prov.phone': 'الهاتف',
  'prov.address': 'العنوان',
  'prov.nhi': 'NHI',
  'prov.verification': 'التحقق',
  'prov.details': 'التفاصيل',
  'prov.suggestion': 'اقتراح',
  'prov.terminology': 'مرجع المصطلحات',
  'prov.searchTerms': 'ابحث في مصطلحات {title}',
  'prov.code': 'الرمز',
  'prov.enter': 'إدخال',
  'prov.selectTermPlaceholder': 'اختر مصطلحًا…',
  'prov.diagnoses': 'التشخيصات',
  'prov.diagnosis': 'التشخيص',
  'prov.medications': 'الأدوية',
  'prov.labs': 'التحاليل',
  'prov.lastVisit': 'آخر زيارة',
  'prov.statusDose': 'الحالة / الجرعة / الجدول',
  'prov.statusSince': 'الحالة / منذ',
  'prov.valueUnitDate': 'القيمة / الوحدة / التاريخ',
  'prov.addDiagnosis': 'إضافة تشخيص',
  'prov.addLab': 'إضافة تحليل',
  'prov.addMedication': 'إضافة دواء',
  'prov.authoritative': 'رسمي معتمد',
  'prov.declared': 'مُعلن ذاتياً',
  'prov.review': 'للمراجعة',
  'prov.strong': 'قوي',
  'prov.lab': 'تحليل',
  'prov.medicationKind': 'دواء',
  'prov.rosterFailed': 'تعذّر تحميل قائمة المرضى. تحقّق من الواجهة البرمجية وحاول مجددًا.',
  'prov.rosterEmpty': 'لا يوجد مرضى مطابقون لبحثك.',
  'prov.retry': 'إعادة المحاولة',
  'prov.offlineSample': 'بيانات نموذجية — عرض دون اتصال',
  'prov.offlineNote': 'الواجهة البرمجية غير متاحة — تُعرض بيانات نموذجية مضمّنة. تُحمّل البيانات الحقيقية عند توفّر الخادم.',
  'prov.savedOffline': 'حُفظ على هذا الجهاز (دون اتصال). لن تظهر التغييرات للمستخدمين الآخرين.',
  'prov.updatedAt': 'آخر تحديث',
  'prov.identityEditable': 'تكتب هذه الحقول مباشرة في السجل المشترك عبر PATCH ‏/patients، وتُرسَل الحقول المتغيّرة فقط.',
  'prov.nameRequired': 'يلزم إدخال اسم واحد على الأقل (عربي أو لاتيني).',
  'prov.savedToServer': 'حُفظ في سجل الخادم.',
  'prov.saving': 'جارٍ الحفظ…',
  'prov.saveFailed': 'فشل الحفظ',
  'prov.patientLoadFailed': 'تعذّر تحميل سجل المريض.',
  'prov.facility': 'المنشأة',
  'prov.mrn': 'الرقم الطبي',
  'prov.status': 'الحالة',
  'prov.registrationSource': 'مصدر التسجيل',
  'prov.med.title': 'الأدوية',
  'prov.med.description': 'الدواء',
  'prov.med.rxnorm': 'المكوّن (RxNorm)',
  'prov.med.dose': 'الجرعة',
  'prov.med.doseUnit': 'الوحدة (UCUM)',
  'prov.med.route': 'طريق الإعطاء',
  'prov.med.form': 'الشكل الصيدلاني',
  'prov.med.frequency': 'التكرار',
  'prov.med.reportedBy': 'المُبلِغ',
  'prov.med.clinician': 'الطبيب',
  'prov.med.patient': 'المريض',
  'prov.med.add': 'إضافة دواء',
  'prov.med.empty': 'لا توجد أدوية مسجّلة.',
  'prov.med.loadFailed': 'تعذّر تحميل الأدوية.',
  'prov.med.deleteConfirm': 'حذف هذا الدواء؟',
  'prov.fh.title': 'التاريخ العائلي',
  'prov.fh.relationship': 'صلة القرابة',
  'prov.fh.relativeName': 'اسم القريب',
  'prov.fh.birthYear': 'سنة الميلاد',
  'prov.fh.condition': 'الحالة (SNOMED)',
  'prov.fh.conditionRequired': 'يلزم إدخال حالة واحدة على الأقل.',
  'prov.fh.add': 'إضافة قريب',
  'prov.fh.empty': 'لا يوجد تاريخ عائلي مسجَّل.',
  'prov.fh.loadFailed': 'تعذّر تحميل التاريخ العائلي.',
  'prov.fh.deleteConfirm': 'حذف سجل هذا القريب؟',
  'prov.delete': 'حذف',
  'demo.allFacilities': 'كل المنشآت',
  'demo.damascus': 'دمشق',
  'demo.mazzeh': 'المزة',
  'demo.baramkeh': 'البرامكة',
  'demo.mouwasatHospital': 'مشفى المواساة الجامعي',
  'demo.damascusCentralLab': 'مختبر دمشق المركزي',
  'demo.type2Diabetes': 'داء السكري من النوع الثاني',
  'demo.hypertension': 'ارتفاع ضغط الدم',
  'demo.ckdStage3': 'مرض الكلى المزمن من المرحلة الثالثة',
  'demo.penicillin': 'البنسلين',
  'demo.peanut': 'الفول السوداني',
  'demo.latex': 'اللاتكس',
  'demo.hemoglobinA1c': 'السكر التراكمي',
  'demo.glucose': 'الغلوكوز',
  'demo.totalCholesterol': 'الكوليسترول الكلي',
  'demo.hemoglobin': 'خضاب الدم',
  'demo.systolicBp': 'ضغط الدم الانقباضي',
  'demo.diastolicBp': 'ضغط الدم الانبساطي',
  'demo.ferritin': 'الفيريتين',
  'demo.vitaminD': 'فيتامين د 25-OH',
  'demo.tsh': 'الهرمون المنبه للدرق',
  'demo.appendectomy': 'استئصال الزائدة الدودية',
  'demo.appendectomyMapping': 'مواءمة استئصال الزائدة الدودية',
  'demo.cardiovascular': 'أمراض القلب والأوعية',
  'demo.endocrine': 'الغدد الصماء',
  'demo.nephrology': 'أمراض الكلى',
  'demo.respiratory': 'أمراض التنفس',
  'demo.hematologic': 'أمراض الدم',
  'demo.infectiousDisease': 'الأمراض الإنتانية',
  'demo.digestive': 'الجهاز الهضمي',
  'demo.generalSurgery': 'الجراحة العامة',
  'demo.drug': 'دوائي',
  'demo.food': 'غذائي',
  'demo.environmental': 'بيئي',
  'demo.hyperlipidemia': 'فرط شحميات الدم',
  'demo.takeWithMeals': 'تُؤخذ حبّة واحدة عن طريق الفم مرتين يوميًا مع الطعام.',
  'demo.takeAtBedtime': 'تُؤخذ حبّة واحدة عن طريق الفم عند النوم.',
  'demo.takeOrallyTwice': 'أتناولها عن طريق الفم مرتين في اليوم.',
  'demo.30days': '30 يومًا',
  'demo.90days': '90 يومًا',
  'demo.60tablets': '60 قرصًا',
  'demo.90tablets': '90 قرصًا',
  'demo.providerRoster': 'سجل مقدّم الرعاية',
  'demo.oral': 'فموي',
  'demo.tablet': 'قرص',
  'demo.bid': 'مرتين يوميًا',
  'demo.qhs': 'عند النوم',
  'demo.blood': 'الدم',
  'demo.serum': 'المصل',
  'demo.cholesterol': 'الكوليسترول',
  'demo.bpSystolic': 'ضغط انقباضي',
  'demo.bpDiastolic': 'ضغط انبساطي',
  'demo.qn': 'كمّي',
  'demo.arterial': 'شرياني',
  'demo.calcidiol': 'كالسيديول',
  'demo.thyrotropin': 'الهرمون المحفز للدرق',
  'demo.myocardialInfarction': 'احتشاء عضلة القلب',
  'demo.asthma': 'الربو',
  'demo.betaThalassemia': 'بيتا ثلاسيميا',
  'demo.hepatitisB': 'التهاب الكبد B',
  'demo.ironDeficiencyAnemia': 'فقر الدم بعوز الحديد',
  'demo.appendicectomy': 'استئصال الزائدة الدودية',
  'demo.cabg': 'مجازة الشريان التاجي',
  'demo.intravenous': 'وريدي',
  'demo.intramuscular': 'عضلي',
  'demo.subcutaneous': 'تحت الجلد',
  'demo.inhalation': 'استنشاقي',
  'demo.rectal': 'شرجي',
  'demo.ophthalmic': 'عيني',
  'demo.otic': 'أذني',
  'demo.transdermal': 'عبر الجلد',
  'demo.capsule': 'كبسولة',
  'demo.solution': 'محلول',
  'demo.suspension': 'معلق',
  'demo.cream': 'كريم',
  'demo.ointment': 'مرهم',
  'demo.patch': 'لصاقة',
  'demo.inhaler': 'بخاخ',
  'demo.injection': 'حقنة',
  'demo.eyeDrops': 'قطرات عين',
  'demo.earDrops': 'قطرات أذن',
  'demo.suppository': 'تحميلة',
  'demo.onceDaily': 'مرة يوميًا',
  'demo.twiceDaily': 'مرتين يوميًا',
  'demo.threeTimesDaily': 'ثلاث مرات يوميًا',
  'demo.fourTimesDaily': 'أربع مرات يوميًا',
  'demo.atBedtime': 'عند النوم',
  'demo.asNeeded': 'عند الحاجة',
  'demo.every4Hours': 'كل 4 ساعات',
  'demo.every6Hours': 'كل 6 ساعات',
  'demo.every8Hours': 'كل 8 ساعات',
  'demo.onceWeekly': 'مرة أسبوعيًا',
  'demo.milligram': 'ميليغرام',
  'demo.microgram': 'ميكروغرام',
  'demo.gram': 'غرام',
  'demo.milliliter': 'ملليلتر',
  'demo.liter': 'لتر',
  'demo.internationalUnit': 'وحدة دولية',
  'demo.percent': 'بالمئة',
  'demo.puff': 'بخة',
  'demo.drop': 'قطرة',
  'demo.unit': 'وحدة',
  'demo.sublingual': 'تحت اللسان',
  'demo.topical': 'موضعي',
  'demo.in': 'مكوّن',
  'demo.min': 'متعدد المكوّنات',
  'demo.asNeededPrn': 'عند الحاجة (PRN)',
  'demo.moiExact': 'تطابق تام في الهوية الشخصية',
  'demo.moiConflict': 'تعارض في الهوية الشخصية',
  'demo.noMatch': 'لا تطابق',
  'demo.phoneMatch': 'تطابق رقم الهاتف',
  'demo.sexMatch': 'تطابق الجنس',
  'demo.latinName': 'الاسم اللاتيني',
  'demo.arabicName': 'الاسم العربي',
  'demo.dob': 'تاريخ الميلاد',
  'demo.moderate': 'متوسطة',
  'demo.completed': 'مكتمل',
  // form placeholders (instructive examples)
  'ph.medNarrative': 'مثال: الميتفورمين، الحبّة البيضاء…',
  'ph.medHowTake': 'مثال: حبّة واحدة عبر الفم مرّتين يوميًا',
  'ph.medName': 'ميتفورمين 500 مغ أقراص فموية',
  'ph.duration': '30 يومًا',
  'ph.quantity': '60 حبّة',
  'ph.indication': 'داء السكري من النوع الثاني',
  'ph.prescriber': 'د. ليلى حدّاد',
  'ph.sigNote': 'تُتناول حبّة واحدة عبر الفم مرّتين يوميًا مع الطعام.',
  'ph.labsName': 'غلوكوز، سكر تراكمي، خضاب الدم',
  'ph.searchRxNorm': 'ابحث في RxNorm',
  'ph.searchSnomed': 'ابحث في SNOMED CT',
  'ph.searchIcd10': 'ابحث في ICD-10 (منظمة الصحة العالمية)',
  'ph.searchProc': 'ابحث في OPCS-4 أو CCAM أو SBCS',
  'ph.allergen': 'البنسلين',
  'ph.procedure': 'استئصال الزائدة الدودية',
  'ph.passportSample': 'مثال: N1234567',
  'ph.governorate': 'دمشق',
  'ph.city': 'المزة',
  'rxn.urticaria': 'شرى',
  'rxn.anaphylaxis': 'تأق (حساسية مفرطة)',
  'rxn.rash': 'طفح',
  'rxn.angioedema': 'وذمة وعائية',

  // explain-my-chart launcher
  'emc.badge': 'اشرح لي ملفي',
  'emc.heading': 'اسأل الملف بلغة موضحة',
  'emc.desc': 'ابحث في ملف صحة {name} بأسلوب حواري للقراءة فقط. تعرض جميع الإجابات الأقسام المرتبطة في ملفك، والعربية تقلب اتجاه اللوحة صميماً لليسار.',
  'emc.open': 'فتح الشرح',
  'search.exampleAr': 'أحمد محمد الحسن',
  'search.exampleLatin': 'أحمد الحسن',

  // patient search
  'search.patients': 'البحث عن مرضى',
  'search.arabicName': 'الاسم بالعربية',
  'search.latinName': 'الاسم باللاتينية',
  'search.dob': 'تاريخ الميلاد',
  'search.searching': 'جارٍ البحث…',
  'search.searchNow': 'بحث',
  'search.none': 'لا يوجد مرضى مطابقون.',
  'search.candidates': 'تم العثور على {n} مرشّح{s}.',
  'search.unnamed': 'بدون اسم',
  'search.dobValue': 'الميلاد {dob} · ',
  'search.nhi': 'الرقم الوطني {nhi}',
  'search.view': 'عرض',
  'search.strong': 'تطابق عالٍ',
  'search.review2': 'يحتاج مراجعة',
  'search.weak': 'تطابق ضعيف',
  // registration
  'reg.title': 'تسجيل مريض',
  'reg.sub': 'مريض جديد · عربي وإنجليزي',
  'reg.closeAria': 'إغلاق نموذج التسجيل',
  'reg.arabicBlock': 'الاسم بالعربية',
  'reg.latinBlock': 'الاسم باللاتينية (إنجليزي)',
  'reg.given': 'الاسم الأول',
  'reg.father': 'اسم الأب',
  'reg.grandfather': 'اسم الجد',
  'reg.family': 'اسم العائلة',
  'reg.identifier': 'المُعرّف',
  'reg.nationalId': 'رقم الهوية',
  'reg.passport': 'جواز السفر',
  'reg.nationalIdField': 'رقم الهوية (11 رقماّ)',
  'reg.passportField': 'رقم جواز السفر',
  'reg.dob': 'تاريخ الميلاد',
  'reg.sex': 'الجنس',
  'reg.male': 'ذكر',
  'reg.female': 'أنثى',
  'reg.phone': 'الهاتف (+963)',
  'reg.governorate': 'المحافظة',
  'reg.city': 'المدينة',
  'reg.registering': 'جارٍ التسجيل…',
  'reg.submit': 'تسجيل المريض',
  'reg.cancel': 'إلغاء',
  // patient banner
  'banner.verifiedAria': 'هوية موثّقة',
  'banner.verifiedTitle': 'الهوية موثّقة — المستوى الرابع',
  'banner.copyNhiAria': 'نسخ الرقم الوطني',
  'banner.level': 'المستوى {n}: {name}',
  'banner.noteBtn': 'ملاحظة',
  'banner.notesBtn': 'الملاحظات',
  'banner.allergies': 'الحساسية',
  'banner.noAllergies': 'لا توجد حساسية معروفة',
  'banner.viewAllergy': 'عرض {name} في قسم الحساسية',
  'banner.directives': 'التوجيهات الطبية المسبقة',
  'banner.viewDirective': 'عرض التوجيه المسبق: {type}',
  'banner.activeOk': 'فعّال',
  'banner.draft': 'مسودة',
  'banner.noneOnFile': 'لا شيء في الملف',
  'banner.latestNote': 'آخر ملاحظة',
  'banner.viewAll': 'عرض الكل ◂',
  'banner.addNote': 'إضافة ملاحظة سريرية',
  'banner.close': 'إغلاق',
  'banner.notePlaceholder': 'اكتب ملاحظة سريرية…',
  'banner.saveNote': 'حفظ الملاحظة',
  // patient profile dialog
  'profile.title': 'ملف المريض',
  'profile.closeAria': 'إغلاق الملف',
  'profile.unnamed': 'بدون اسم',
  'profile.nhi': 'الرقم الوطني {nhi}',
  'profile.dob': 'تاريخ الميلاد',
  'profile.sex': 'الجنس',
  'profile.phone': 'الهاتف',
  'profile.mrn': 'رقم الملف',
  'profile.facility': 'المنشأة',
  'profile.status': 'الحالة',
  'profile.tier': 'مستوى التحقق: المستوى {n} — {name}',
  // portable proof / encounters / documents
  'proof.aria': 'بطاقة التطعيم المحمولة',
  'proof.title': 'إثبات التطعيم',
  'proof.std': 'بطاقة الصحة الذكية · رابط الصحة الذكية',
  'proof.shc': 'بطاقة الصحة الذكية',
  'proof.shcDesc': 'إثبات تطعيم كوفيد-19 قابل للتحقق دون اتصال — امسح الرمز للتأكد من توقيع الجهة المُصدِرة دون الحاجة إلى خادم.',
  'proof.shcAria': 'فتح رمز بطاقة الصحة الذكية لكوفيد-19',
  'proof.shcImg': 'رمز بطاقة الصحة الذكية لكوفيد-19',
  'proof.viewQr': 'عرض الرمز',
  'proof.shl': 'رابط الصحة الذكية',
  'proof.shlDesc': 'سجل التطعيم الكامل خلف رابط مشفّر محمي برمز مرور (يبقى صالحاً 48 ساعة).',
  'proof.shlAria': 'فتح رمز رابط الصحة الذكية',
  'proof.shlImg': 'رمز رابط الصحة الذكية',
  'proof.close': 'إغلاق',
  'proof.shareLink': 'رابط المشاركة',
  'proof.copyLinkAria': 'نسخ رابط المشاركة',
  'proof.shcModalHint': 'امسح الرمز للتحقق من سجل تطعيم كوفيد-19 دون اتصال. البطاقة موقّعة من الجهة المُصدِرة (ES256) ولا تحتوي إلا على تاريخ ميلاد المريض وحقائق التطعيم لكل جرعة.',
  'proof.shlModalHint': 'رمز المرور {code} (للتطوير فقط). ينتهي الرابط بعد 48 ساعة من إنشائه.',
  'proof.encountersAria': 'الزيارات الأخيرة',
  'proof.encounters': 'الزيارات الأخيرة',
  'proof.nVisits': '{n} زيارات',
  'proof.documentsAria': 'المستندات',
  'proof.documents': 'المستندات',
  'proof.nFiles': '{n} ملفات',
  'proof.pdf': 'PDF',
  'proof.image': 'صورة',
  'enc.emergency': 'طوارئ',
  'enc.outpatient': 'عيادة خارجية',
  'enc.inpatient': 'إقامة',
  'enc.telehealth': 'طب اتصالي',
  'enc.home': 'منزل',
  'enc.refer': 'تحويل',
  'enc.admitted': 'إدخال',
  'enc.reason.chest': 'ألم في الصدر، لاستبعاد المتلازمة التاجية الحادة',
  'enc.loc.mouwasat_er': 'مشفى المواساة الجامعي · الطوارئ',
  'enc.loc.mouwasat_endo': 'مشفى المواساة الجامعي · الغدد الصماء',
  'enc.loc.telehealth': 'طب اتصالي',
  'enc.reason.diabetes': 'متابعة السكري (السكر التراكمي 7.4%)',
  'enc.reason.adherence': 'مراجعة الالتزام بتناول الأدوية',
  'doc.discharge-summary': 'ملخص خروج',
  'doc.lab-report': 'تحليل مخبري',
  'doc.imaging-report': 'تقرير تصويري',
  'doc.referral-letter': 'كتاب تحويل',
  'doc.scanned-paper': 'ورقة ممسوحة',
  'doc.consent': 'موافقة',
  'doc.insurance': 'تأمين',
  'doc.other': 'أخرى',
  'doc.title.discharge': 'ملخص خروج — زيارة الطوارئ (2026-08-27)',
  'doc.title.lab': 'تحليل مخبري ممسوح — صورة الدم الكاملة + السكر التراكمي',
  'doc.title.xray': 'تقرير صورة الصدر بالأشعة',

  // صفحة تسجيل الدخول
  'login.title': 'السجل الصحي السوري',
  'login.subtitle': 'السجل الصحي الشخصي الوطني',
  'login.rolePrompt': 'اختر دورك',
  'login.role.patient': 'مريض',
  'login.role.patientDesc': 'الوصول إلى سجلك الصحي الشخصي',
  'login.role.provider': 'مقدّم رعاية',
  'login.role.providerDesc': 'مساحة عمل سريرية للمهنيين الصحيين',
  'login.username': 'اسم المستخدم',
  'login.usernamePlaceholder': 'أدخل اسم المستخدم',
  'login.password': 'كلمة المرور',
  'login.passwordPlaceholder': 'أدخل كلمة المرور',
  'login.submit': 'تسجيل الدخول',
  'login.signingIn': 'جارٍ تسجيل الدخول…',
  'login.error': 'اسم المستخدم أو كلمة المرور غير صحيحة',
  'login.langAria': 'تبديل اللغة',
  'login.footer': 'نسخة تجريبية — استخدم admin / admin',
  'login.noNhi': 'ليس لديك رقم صحّي؟',
  'login.registerNhi': 'سجّل الآن',

  // immunizations panel (patient view)
  'imm.title': 'التمنيع (اللقاحات)',
  'imm.aria': 'التمنيع (اللقاحات)',
  'imm.loading': 'جارٍ تحميل اللقاحات…',
  'imm.empty': 'لا توجد سجلات تمنيع بعد.',
  'imm.doseOf': 'الجرعة {n}',
  'imm.cvxTitle': 'رمز اللقاح CVX: {code}',
  'imm.selfReported': 'بلاغ ذاتي',
  'imm.status.complete': 'مكتملة',
  'imm.status.partial': 'جزئية',
  'imm.status.notDone': 'لم تُعطَ',
  'imm.status.error': 'خطأ في الإدخال',
  'imm.status.recorded': 'مسجّلة',
  'imm.recommendations': 'توصيات التمنيع',
  'imm.caveat':
    'مبني على جدول منظمة الصحة العالمية المرجعي — طبيبك هو من يؤكد جميع الجرعات. هذا ليس الجدول الوطني السوري المعتمد من وزارة الصحة/برنامج التمنيع الموسع بعد.',
  'imm.dueNow': 'مستحق الآن',
  'imm.dueOn': 'مستحق في',
  'imm.notYetEligible': 'غير مؤهل بعد حتى',
  'imm.seriesComplete': 'السلسلة مكتملة',
  'imm.boosterNote': 'جرعة معززة',
  // devices panel (patient view)
  'dev.title': 'الأجهزة الطبية',
  'dev.aria': 'الأجهزة الطبية — الزروع والمعدات السريرية',
  'dev.loading': 'جارٍ تحميل الأجهزة…',
  'dev.empty': 'لا توجد أجهزة مسجّلة.',
  'dev.implantedAria': 'جهاز مزروع — قد يؤثر على التصوير بالرنين المغناطيسي أو الجراحة أو الطوارئ',
  'dev.implantedOn': 'زُرع في',
  'dev.removedOn': 'أُزيل في',
  'dev.detailsAria': 'إظهار/إخفاء تفاصيل الجهاز',
  'dev.field.udi': 'المعرّف الفريد للجهاز UDI-DI',
  'dev.field.serial': 'الرقم التسلسلي / الدفعة',
  'dev.field.model': 'اسم/طراز الجهاز',
  'dev.field.manufacturer': 'الشركة المصنعة',
  'dev.field.site': 'موقع الجسم',
  'dev.field.indication': 'دواعي الاستخدام',
  'dev.field.notes': 'ملاحظات',
  'dev.mri.safe': 'آمن للرنين المغناطيسي',
  'dev.mri.conditional': 'مشروط للرنين المغناطيسي',
  'dev.mri.unsafe': 'غير آمن للرنين المغناطيسي',
  'dev.mri.unknown': 'حالة الرنين غير معروفة',
  'dev.status.active': 'نشط',
  'dev.status.removed': 'مُزال',
  'dev.status.replaced': 'مُستبدل',
  'dev.status.inactive': 'غير نشط',
}

const STRINGS: Record<Lang, Dict> = { en, ar }

/* ---------- store ---------- */

function readInitial(): Lang {
  if (typeof window === 'undefined') return 'en'
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY)
    if (saved === 'en' || saved === 'ar') return saved
  } catch {
    /* private mode etc. */
  }
  return 'en'
}

let lang: Lang = readInitial()
const listeners = new Set<() => void>()

function applyDocumentDirection(): void {
  if (typeof document === 'undefined') return
  document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
  document.documentElement.lang = lang
}
applyDocumentDirection()

export function getLang(): Lang {
  return lang
}

export function setLang(next: Lang): void {
  if (next === lang) return
  lang = next
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    /* ignore persistence failures */
  }
  applyDocumentDirection()
  listeners.forEach((fn) => fn())
}

export function toggleLang(): void {
  setLang(lang === 'ar' ? 'en' : 'ar')
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** Subscribe a component to language changes (re-renders on switch). */
export function useLang(): Lang {
  return useSyncExternalStore(subscribe, getLang, () => 'en' as Lang)
}

/* ---------- lookup ---------- */

/** Translate a key for the current language. Falls back to English, then the key. */
export function t(key: string, langOverride?: Lang): string {
  const l = langOverride ?? lang
  return STRINGS[l]?.[key] ?? STRINGS.en[key] ?? key
}

/** Translate + interpolate: fmt(t('x'), { n: 3 }) replaces {n}. */
export function fmt(text: string, params: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name]) : match))
}

/** Translate a raw status/severity vocabulary value (data is stored in English). */
const STATUS_KEYS: Record<string, string> = {
  active: 'status.active',
  paused: 'status.paused',
  completed: 'status.completed',
  stopped: 'status.stopped',
  resolved: 'status.resolved',
  historic: 'status.historic',
  inactive: 'status.inactive',
  'entered in error': 'status.enteredInError',
  mild: 'status.mild',
  moderate: 'status.moderate',
  severe: 'status.severe',
  'life-threatening': 'status.lifeThreatening',
  planned: 'status.planned',
  cancelled: 'status.cancelled',
  draft: 'status.draft',
}

export function statusLabel(value: string): string {
  const key = STATUS_KEYS[value.toLowerCase().trim()]
  return key ? t(key) : value
}

/** Map a (possibly translated) status label back to its canonical English value. */
export function statusFromLabel(value: string): string | null {
  const v = value.trim().toLowerCase()
  for (const [en, key] of Object.entries(STATUS_KEYS)) {
    if (en.toLowerCase() === v || t(key).toLowerCase() === v) return en
  }
  return null
}

const SEX_KEYS: Record<string, string> = {
  m: 'banner.male',
  male: 'banner.male',
  f: 'banner.female',
  female: 'banner.female',
}

const DEMO_TEXT_KEYS: Record<string, string> = {
  'all facilities': 'demo.allFacilities',
  damascus: 'demo.damascus',
  mazzeh: 'demo.mazzeh',
  baramkeh: 'demo.baramkeh',
  'al-mouwasat university hospital': 'demo.mouwasatHospital',
  'damascus central lab': 'demo.damascusCentralLab',
  'type 2 diabetes mellitus': 'demo.type2Diabetes',
  hypertension: 'demo.hypertension',
  'chronic kidney disease stage 3': 'demo.ckdStage3',
  penicillin: 'demo.penicillin',
  peanut: 'demo.peanut',
  latex: 'demo.latex',
  'hemoglobin a1c': 'demo.hemoglobinA1c',
  glucose: 'demo.glucose',
  'total cholesterol': 'demo.totalCholesterol',
  hemoglobin: 'demo.hemoglobin',
  'systolic blood pressure': 'demo.systolicBp',
  'diastolic blood pressure': 'demo.diastolicBp',
  ferritin: 'demo.ferritin',
  'vitamin d 25-oh': 'demo.vitaminD',
  tsh: 'demo.tsh',
  appendectomy: 'demo.appendectomy',
  'appendectomy mapping': 'demo.appendectomyMapping',
  cardiovascular: 'demo.cardiovascular',
  endocrine: 'demo.endocrine',
  nephrology: 'demo.nephrology',
  respiratory: 'demo.respiratory',
  hematologic: 'demo.hematologic',
  'infectious disease': 'demo.infectiousDisease',
  digestive: 'demo.digestive',
  'general surgery': 'demo.generalSurgery',
  drug: 'demo.drug',
  food: 'demo.food',
  environmental: 'demo.environmental',
  hyperlipidemia: 'demo.hyperlipidemia',
  'take one tablet by mouth twice daily with meals.': 'demo.takeWithMeals',
  'take one tablet by mouth at bedtime.': 'demo.takeAtBedtime',
  'i take it orally twice a day.': 'demo.takeOrallyTwice',
  '30 days': 'demo.30days',
  '90 days': 'demo.90days',
  '60 tablets': 'demo.60tablets',
  '90 tablets': 'demo.90tablets',
  'provider roster': 'demo.providerRoster',
  oral: 'demo.oral',
  tablet: 'demo.tablet',
  bid: 'demo.bid',
  qhs: 'demo.qhs',
  blood: 'demo.blood',
  serum: 'demo.serum',
  cholesterol: 'demo.cholesterol',
  'bp systolic': 'demo.bpSystolic',
  'bp diastolic': 'demo.bpDiastolic',
  qn: 'demo.qn',
  arterial: 'demo.arterial',
  calcidiol: 'demo.calcidiol',
  thyrotropin: 'demo.thyrotropin',
  'myocardial infarction': 'demo.myocardialInfarction',
  asthma: 'demo.asthma',
  'beta-thalassemia': 'demo.betaThalassemia',
  'hepatitis b': 'demo.hepatitisB',
  'iron deficiency anemia': 'demo.ironDeficiencyAnemia',
  appendicectomy: 'demo.appendicectomy',
  'coronary artery bypass graft': 'demo.cabg',
  intravenous: 'demo.intravenous',
  intramuscular: 'demo.intramuscular',
  subcutaneous: 'demo.subcutaneous',
  inhalation: 'demo.inhalation',
  rectal: 'demo.rectal',
  ophthalmic: 'demo.ophthalmic',
  otic: 'demo.otic',
  transdermal: 'demo.transdermal',
  capsule: 'demo.capsule',
  solution: 'demo.solution',
  suspension: 'demo.suspension',
  cream: 'demo.cream',
  ointment: 'demo.ointment',
  patch: 'demo.patch',
  inhaler: 'demo.inhaler',
  injection: 'demo.injection',
  'eye drops': 'demo.eyeDrops',
  'ear drops': 'demo.earDrops',
  suppository: 'demo.suppository',
  sublingual: 'demo.sublingual',
  topical: 'demo.topical',
  in: 'demo.in',
  min: 'demo.min',
  'as needed (prn)': 'demo.asNeededPrn',
  'once daily': 'demo.onceDaily',
  'twice daily': 'demo.twiceDaily',
  'three times daily': 'demo.threeTimesDaily',
  'four times daily': 'demo.fourTimesDaily',
  'at bedtime': 'demo.atBedtime',
  'as needed': 'demo.asNeeded',
  'every 4 hours': 'demo.every4Hours',
  'every 6 hours': 'demo.every6Hours',
  'every 8 hours': 'demo.every8Hours',
  'once weekly': 'demo.onceWeekly',
  milligram: 'demo.milligram',
  microgram: 'demo.microgram',
  gram: 'demo.gram',
  milliliter: 'demo.milliliter',
  liter: 'demo.liter',
  'international unit': 'demo.internationalUnit',
  percent: 'demo.percent',
  puff: 'demo.puff',
  drop: 'demo.drop',
  unit: 'demo.unit',
}

export function sexLabel(value: string): string {
  const key = SEX_KEYS[value.trim().toLowerCase()]
  return key ? t(key) : value
}

export function tierShortLabel(tier: number): string {
  return t(`tier.short${tier}`)
}

export function portalActionLabel(value: string): string {
  const key = value === 'created_new'
    ? 'portal.action.createdNew'
    : value === 'linked_existing'
      ? 'portal.action.linkedExisting'
      : ''
  return key ? t(key) : value
}

export function demoText(value: string): string {
  const direct = DEMO_TEXT_KEYS[value.trim().toLowerCase()]
  if (direct) return t(direct)
  return value
}

/**
 * Human-readable, localized label for an MPI match reason. Deterministic
 * reasons map to fixed keys; probabilistic composites like
 * "latin_name=0.95; dob=1.00" render as "الاسم اللاتيني 0.95 · تاريخ الميلاد 1.00".
 */
export function matchReasonLabel(value: string): string {
  const raw = value.trim()
  if (!raw) return value
  if (raw === 'moi_id exact') return t('demo.moiExact')
  if (raw === 'moi_id conflict') return t('demo.moiConflict')
  if (raw === 'no_match') return t('demo.noMatch')
  const parts = raw.split(';').map((part) => {
    const piece = part.trim()
    if (piece === 'phone_match') return t('demo.phoneMatch')
    if (piece === 'sex_match') return t('demo.sexMatch')
    const split = piece.split('=')
    if (split.length === 2) {
      const field = split[0].trim()
      const num = split[1].trim()
      const label =
        field === 'latin_name'
          ? t('demo.latinName')
          : field === 'arabic_name'
            ? t('demo.arabicName')
            : field === 'dob'
              ? t('demo.dob')
              : field
      return `${label} ${num}`
    }
    return piece
  })
  return parts.join(' · ')
}

/** Translate a raw vocabulary token (kebab/snake) via `enc.*` / `doc.*` / `rxn.*` namespaces. */
export function vocab(value: string): string {
  const key = value.trim().toLowerCase().replace(/\s+/g, '-')
  for (const ns of ['enc', 'doc', 'rxn']) {
    const candidate = `${ns}.${key}`
    const table = STRINGS[getLang()]
    if (candidate in table) return table[candidate]
    if (candidate in STRINGS.en) return STRINGS.en[candidate]
  }
  return demoText(value)
}

/** Domain title lookup (e.g. 'labs' → domain.labs.title). */
export const dTitle = (key: string): string => t(`domain.${key}.title`)
/** Domain subtitle lookup (e.g. 'labs' → domain.labs.subtitle). */
export const dSub = (key: string): string => t(`domain.${key}.subtitle`)
/** Field-label lookup (prefixed `field.`). */
export const tf = (key: string): string => t(`field.${key}`)
/** Domain chrome lookup (prefixed `domain.`). */
export const th = (key: string): string => t(`domain.${key}`)
/** Nav label lookup (prefixed `nav.`). */
export const tn = (key: string): string => t(`nav.${key}`)
/** Dashboard label lookup (prefixed `dash.`). */
export const tl = (key: string): string => t(`dash.${key}`)
